import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaChevronLeft,
  FaChevronRight,
  FaArrowRight,
  FaBookOpen,
  FaChalkboardTeacher,
  FaEdit,
  FaFolderPlus,
  FaLayerGroup,
  FaListOl,
  FaInfoCircle,
  FaPlus,
  FaProjectDiagram,
  FaSave,
  FaTrash,
  FaUserGraduate,
  FaUsersCog,
  FaClipboardCheck,
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import { utils as XLSXUtils, writeFile as writeXLSXFile } from '@redoper1/xlsx-js-style';
import CourseService from '../services/CourseService';
import api from '../api';
import {
  CONTENT_CREATOR_PERMISSION,
  COURSE_EDITOR_VIEW_PERMISSION,
  COURSE_CLASS_MANAGER_PERMISSION,
  COURSE_COORDINATOR_PERMISSION,
  hasPermission,
  isAdmin,
} from '../utils/permissions';
import { useAuth } from '../AuthContext';
import { resolveAssetUrl } from '../utils/assets';
import {
  COURSE_CATALOG_PAGE_SIZE,
  countAttendanceStatuses,
  filterAndSortCourses,
  formatAttendanceStatus,
  formatCourseDate,
  formatCourseDuration,
  formatCourseStatus,
  getCourseDurationMinutes,
  paginateCourseCatalog,
} from './courseUiUtils';

const emptyLesson = () => ({
  title: '',
  description: '',
  durationMinutes: 30,
  materials: [],
  tasks: [],
  quiz: null,
});
const emptyQuestion = (type = 'text') => ({
  prompt: '',
  type,
  score: 1,
  options: type === 'multiple_choice' ? [''] : [],
  correctOptionIndex: type === 'multiple_choice' ? 0 : null,
  acceptedKeywords: [],
});
const emptyModule = () => ({ title: '', description: '', lessons: [emptyLesson()] });
const createEmptyCourseForm = () => ({
  title: '',
  description: '',
  coverImageUrl: '',
  estimatedHours: 8,
  difficulty: 'iniciante',
  activityNotificationEmail: '',
  prerequisiteCourseIds: [],
  certificateConfig: {
    companyName: '',
    cnpj: '',
    address: '',
    contact: '',
    logoUrl: '',
    signerName: '',
    primaryColor: '#0f766e',
    logoPosition: 'center',
    bodyTextTemplate: 'Certificamos que {{ALUNO}} concluiu com êxito o curso {{CURSO}}, com carga horária de {{CARGA_HORARIA}} horas.',
  },
  requiresAccessGrant: false,
  modules: [emptyModule()],
});

const cloneCourseToForm = (course) => ({
  title: course?.title || '',
  description: course?.description || '',
  coverImageUrl: course?.coverImageUrl || '',
  estimatedHours: Number(course?.estimatedHours || 8),
  difficulty: course?.difficulty || 'iniciante',
  activityNotificationEmail: course?.activityNotificationEmail || '',
  prerequisiteCourseIds: Array.isArray(course?.prerequisiteCourseIds) ? [...course.prerequisiteCourseIds] : [],
  certificateConfig: {
    companyName: course?.certificateConfig?.companyName || '',
    cnpj: course?.certificateConfig?.cnpj || '',
    address: course?.certificateConfig?.address || '',
    contact: course?.certificateConfig?.contact || '',
    logoUrl: course?.certificateConfig?.logoUrl || '',
    signerName: course?.certificateConfig?.signerName || '',
    primaryColor: course?.certificateConfig?.primaryColor || '#0f766e',
    logoPosition: course?.certificateConfig?.logoPosition || 'center',
    bodyTextTemplate:
      course?.certificateConfig?.bodyTextTemplate ||
      'Certificamos que {{ALUNO}} concluiu com êxito o curso {{CURSO}}, com carga horária de {{CARGA_HORARIA}} horas.',
  },
  requiresAccessGrant: Boolean(course?.requiresAccessGrant),
  modules:
    Array.isArray(course?.modules) && course.modules.length > 0
      ? course.modules.map((module) => ({
          id: module.id,
          title: module.title,
          description: module.description || '',
          order: module.order,
          lessons:
            Array.isArray(module.lessons) && module.lessons.length > 0
              ? module.lessons.map((lesson) => ({
                  id: lesson.id,
                  title: lesson.title,
                  description: lesson.description || '',
                  order: lesson.order,
                  durationMinutes: Number(lesson.durationMinutes || 30),
                  materials: Array.isArray(lesson.materials) ? [...lesson.materials] : [],
                  tasks: Array.isArray(lesson.tasks)
                    ? lesson.tasks.map((task) => ({
                        ...task,
                        formQuestions: Array.isArray(task.formQuestions)
                          ? task.formQuestions.map((question) => ({ ...question }))
                          : [],
                      }))
                    : [],
                  quiz: lesson.quiz
                    ? {
                        ...lesson.quiz,
                        questions: Array.isArray(lesson.quiz.questions)
                          ? lesson.quiz.questions.map((question) => ({ ...question }))
                          : [],
                      }
                    : null,
                }))
              : [emptyLesson()],
        }))
      : [emptyModule()],
});

const fileTypeLabel = (mimeType = '') => {
  const normalized = String(mimeType || '').toLowerCase();
  if (normalized.includes('pdf')) return 'pdf';
  if (normalized.includes('video')) return 'video';
  if (normalized.includes('image')) return 'imagem';
  if (normalized.includes('word') || normalized.includes('document')) return 'word';
  return 'arquivo';
};
const isVideoFile = (file) => {
  const mime = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return (
    mime.includes('video') ||
    name.endsWith('.mp4') ||
    name.endsWith('.webm') ||
    name.endsWith('.ogg') ||
    name.endsWith('.mov') ||
    name.endsWith('.avi') ||
    name.endsWith('.mkv')
  );
};
export const getMaterialSourceUrl = (material = {}) =>
  String(material?.url || material?.downloadUrl || material?.relativePath || material?.downloadPath || '').trim();
const isYouTubeHost = (value = '') => {
  try {
    const parsed = new URL(String(value || '').trim());
    const hostname = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    return hostname === 'youtu.be' || hostname.endsWith('youtube.com');
  } catch (error) {
    return false;
  }
};
const isVideoMaterial = (material = {}) => {
  const name = String(material?.name || '').toLowerCase();
  const mime = String(material?.mimeType || '').toLowerCase();
  const url = getMaterialSourceUrl(material);
  const normalizedUrl = url.toLowerCase();
  const hasYoutubeVideoId = Boolean(getYouTubeVideoId(url));
  return (
    String(material?.type || '').toLowerCase() === 'video' ||
    mime.includes('video') ||
    name.endsWith('.mp4') ||
    name.endsWith('.webm') ||
    name.endsWith('.ogg') ||
    hasYoutubeVideoId ||
    normalizedUrl.endsWith('.mp4') ||
    normalizedUrl.endsWith('.webm') ||
    normalizedUrl.endsWith('.ogg') ||
    normalizedUrl.endsWith('.mov')
  );
};
const getYouTubeVideoId = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^[a-zA-Z0-9_-]{11}$/.test(raw)) return raw;
  try {
    const parsed = new URL(raw);
    const hostname = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (hostname === 'youtu.be') {
      return parsed.pathname.replace('/', '').split('/')[0] || '';
    }
    if (hostname.endsWith('youtube.com')) {
      if (parsed.pathname === '/watch') {
        return parsed.searchParams.get('v') || '';
      }
      if (parsed.pathname.startsWith('/shorts/')) {
        return parsed.pathname.replace('/shorts/', '').split('/')[0] || '';
      }
      if (parsed.pathname.startsWith('/embed/')) {
        return parsed.pathname.replace('/embed/', '').split('/')[0] || '';
      }
      if (parsed.pathname.startsWith('/live/')) {
        return parsed.pathname.replace('/live/', '').split('/')[0] || '';
      }
    }
  } catch (error) {
    return '';
  }
  return '';
};
const isYoutubeMaterial = (material = {}) =>
  Boolean(
    getYouTubeVideoId(String(material?.url || material?.downloadUrl || '')),
  );
const getYouTubeEmbedUrl = (material = {}) => {
  const videoId = getYouTubeVideoId(String(material?.url || material?.downloadUrl || ''));
  return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
};
const isImageMaterial = (material = {}) => {
  const name = String(material?.name || '').toLowerCase();
  const mime = String(material?.mimeType || '').toLowerCase();
  return mime.includes('image') || /\.(png|jpe?g|gif|webp|svg)$/.test(name);
};
const isPdfMaterial = (material = {}) => {
  const name = String(material?.name || '').toLowerCase();
  const mime = String(material?.mimeType || '').toLowerCase();
  return mime.includes('pdf') || name.endsWith('.pdf');
};


const isInlineOrBlobUrl = (value = '') => /^(data:|blob:)/i.test(String(value || '').trim());
const isExternalHttpUrl = (value = '') => {
  const raw = String(value || '').trim();
  if (!/^https?:\/\//i.test(raw)) return false;
  try {
    const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(raw);
    const apiBase = new URL(api?.defaults?.baseURL || fallbackOrigin, fallbackOrigin);
    return parsed.origin !== apiBase.origin && parsed.origin !== fallbackOrigin;
  } catch {
    return true;
  }
};
const isProtectedCourseReference = (value = '') => {
  const raw = String(value || '').trim();
  return Boolean(raw) && !isInlineOrBlobUrl(raw) && !isExternalHttpUrl(raw) && !isYoutubeMaterial({ url: raw });
};
const preferredStoredUploadReference = (data = {}) =>
  data?.relativePath || data?.downloadPath || data?.downloadUrl || data?.url || '';

const useProtectedBlobUrl = (endpoint) => {
  const [blobUrl, setBlobUrl] = useState('');
  useEffect(() => {
    let active = true;
    let currentUrl = '';
    setBlobUrl('');
    if (!endpoint) return undefined;
    api.get(endpoint, { responseType: 'blob' })
      .then((response) => {
        currentUrl = URL.createObjectURL(response.data);
        if (!active) {
          URL.revokeObjectURL(currentUrl);
          return;
        }
        setBlobUrl(currentUrl);
      })
      .catch(() => {
        if (active) setBlobUrl('');
      });
    return () => {
      active = false;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [endpoint]);
  return blobUrl;
};

const ProtectedCourseAsset = ({ endpoint, fallbackUrl = '', children }) => {
  const protectedUrl = useProtectedBlobUrl(endpoint);
  const directUrl = fallbackUrl ? resolveAssetUrl(fallbackUrl) : '';
  return children(protectedUrl || directUrl);
};

const CourseCover = ({ course, className, alt }) => {
  const source = course?.coverImageUrl || '';
  if (!source) return null;
  const endpoint = isProtectedCourseReference(source) && course?.id ? `/courses/${course.id}/cover/content` : '';
  return (
    <ProtectedCourseAsset endpoint={endpoint} fallbackUrl={endpoint ? '' : source}>
      {(url) => url ? <img src={url} alt={alt || `Capa do curso ${course?.title || ''}`} className={className} /> : null}
    </ProtectedCourseAsset>
  );
};

const CertificateLogoPreview = ({ courseId, logoUrl, className }) => {
  if (!logoUrl) return null;
  const endpoint = isProtectedCourseReference(logoUrl) && courseId ? `/courses/${courseId}/certificate-logo/content` : '';
  return (
    <ProtectedCourseAsset endpoint={endpoint} fallbackUrl={endpoint ? '' : logoUrl}>
      {(url) => url ? <img src={url} alt="Logo do certificado" className={className} /> : null}
    </ProtectedCourseAsset>
  );
};

const CourseMaterialPreview = ({ courseId, material }) => {
  const source = getMaterialSourceUrl(material);
  const endpoint = isProtectedCourseReference(source) && courseId && material?.id ? `/courses/${courseId}/materials/${material.id}/content` : '';
  return (
    <ProtectedCourseAsset endpoint={endpoint} fallbackUrl={endpoint ? '' : source}>
      {(materialUrl) => (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 sm:p-3">
          <p className="mb-2 truncate text-sm font-semibold text-slate-700">{material.name}</p>
          {isYoutubeMaterial(material) ? (
            <div className="aspect-video w-full overflow-hidden rounded bg-black">
              <iframe src={getYouTubeEmbedUrl(material)} title={material.name || 'Vídeo do YouTube'} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
            </div>
          ) : null}
          {!isYoutubeMaterial(material) && materialUrl && isVideoMaterial(material) ? <video controls playsInline src={materialUrl} className="max-h-[50vh] w-full rounded bg-black object-contain sm:max-h-[70vh]">Seu navegador não suporta vídeo.</video> : null}
          {materialUrl && isImageMaterial(material) ? <img src={materialUrl} alt={material.name} className="max-h-[50vh] w-full rounded object-contain bg-white sm:max-h-[70vh]" /> : null}
          {materialUrl && isPdfMaterial(material) ? <iframe src={materialUrl} title={`Prévia ${material.name}`} className="h-[55vh] min-h-[280px] w-full rounded border border-slate-200 bg-white sm:h-[70vh] sm:min-h-[420px]" /> : null}
          {!isYoutubeMaterial(material) && !isVideoMaterial(material) && !isImageMaterial(material) && !isPdfMaterial(material) ? <div className="rounded border border-dashed border-slate-300 bg-white p-3 text-xs text-slate-600">Prévia indisponível para este tipo de arquivo. Use o botão abaixo para abrir/download.</div> : null}
          {materialUrl ? <a href={materialUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100">Abrir material</a> : null}
        </div>
      )}
    </ProtectedCourseAsset>
  );
};

const getFormDurationMinutes = (form) =>
  (form?.modules || []).reduce(
    (acc, module) =>
      acc +
      (module.lessons || []).reduce(
        (lessonAcc, lesson) => lessonAcc + Math.max(1, Number(lesson.durationMinutes || 0)),
        0,
      ),
    0,
  );

const TABS = {
  CATALOG: 'catalog',
  ENROLLED: 'enrolled',
  LEARNING_PATHS: 'learning-paths',
  CREATOR: 'creator',
  COORDINATOR: 'coordinator',
  TEACHER: 'teacher',
  CLASS_DIARY: 'class-diary',
  CERTIFICATE: 'certificate',
};

export default function Courses() {
  const { user } = useAuth();
  const userIsAdmin = isAdmin(user);
  const canCreate = useMemo(
    () => userIsAdmin || hasPermission(user, CONTENT_CREATOR_PERMISSION),
    [user, userIsAdmin],
  );
  const canCoordinate = useMemo(
    () =>
      userIsAdmin ||
      hasPermission(user, COURSE_COORDINATOR_PERMISSION) ||
      hasPermission(user, COURSE_CLASS_MANAGER_PERMISSION),
    [user, userIsAdmin],
  );
  const canViewEditor = useMemo(
    () => userIsAdmin || hasPermission(user, COURSE_EDITOR_VIEW_PERMISSION) || canCoordinate,
    [userIsAdmin, user, canCoordinate],
  );
  const canReviewActivities = useMemo(
    () => (canCoordinate || canCreate || userIsAdmin) && !hasPermission(user, 'ALUNO'),
    [canCoordinate, canCreate, userIsAdmin, user],
  );
  const canEditAttendance = useMemo(
    () => userIsAdmin || hasPermission(user, 'PROFESSOR'),
    [userIsAdmin, user],
  );
  const canAccessClassDiary = useMemo(
    () => canCoordinate || canEditAttendance,
    [canCoordinate, canEditAttendance],
  );

  const [activeTab, setActiveTab] = useState(TABS.ENROLLED);
  const [courses, setCourses] = useState([]);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
  const [catalogFilter, setCatalogFilter] = useState('all');
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [catalogSort, setCatalogSort] = useState('title');
  const [catalogPage, setCatalogPage] = useState(1);
  const [courseActionId, setCourseActionId] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [selectedCourseDetailTab, setSelectedCourseDetailTab] = useState('trilha');
  const [isLearningOverlayOpen, setIsLearningOverlayOpen] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [classrooms, setClassrooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [studentUsers, setStudentUsers] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [classroomForm, setClassroomForm] = useState({
    name: '',
    description: '',
    status: 'ACTIVE',
    coordinatorUserId: user?.id || null,
    studentIds: [],
    courseIds: [],
  });
  const [editingClassroomId, setEditingClassroomId] = useState(null);
  const [grantForm, setGrantForm] = useState({
    courseId: null,
    userId: null,
    accessStartAt: '',
    accessEndAt: '',
  });
  const [form, setForm] = useState(createEmptyCourseForm);
  const [creatorPanel, setCreatorPanel] = useState('overview');
  const [expandedCreatorModules, setExpandedCreatorModules] = useState({ 0: true });
  const [expandedCreatorLessons, setExpandedCreatorLessons] = useState({ '0:0': true });
  const [progressByCourse, setProgressByCourse] = useState({});
  const [taskAnswers, setTaskAnswers] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [taskAttachments, setTaskAttachments] = useState({});
  const [instructorSubmissions, setInstructorSubmissions] = useState([]);
  const [attendanceClassrooms, setAttendanceClassrooms] = useState([]);
  const [classDiaries, setClassDiaries] = useState([]);
  const [selectedDiaryId, setSelectedDiaryId] = useState(null);
  const [editingDiaryId, setEditingDiaryId] = useState(null);
  const [isRegisteringFrequency, setIsRegisteringFrequency] = useState(false);
  const [attendanceClassroomId, setAttendanceClassroomId] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().slice(0, 10));
  const [diaryTheme, setDiaryTheme] = useState('');
  const [diaryObjective, setDiaryObjective] = useState('');
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [selectedAttendanceStudentId, setSelectedAttendanceStudentId] = useState(null);
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [attendanceExporting, setAttendanceExporting] = useState(false);
  const [attendanceFilters, setAttendanceFilters] = useState({
    classroomId: 'all',
    studentId: 'all',
    teacherId: 'all',
  });
  const [appliedAttendanceFilters, setAppliedAttendanceFilters] = useState({
    classroomId: 'all',
    studentId: 'all',
    teacherId: 'all',
  });
  const [hasQueriedAttendance, setHasQueriedAttendance] = useState(false);
  const [diaryQueryFilters, setDiaryQueryFilters] = useState({
    startDate: '',
    endDate: '',
    teacherId: 'all',
    theme: '',
  });
  const [hasQueriedDiaries, setHasQueriedDiaries] = useState(false);
  const [certificateQuery, setCertificateQuery] = useState('');
  const [certificateResult, setCertificateResult] = useState(null);
  const [instructorFilters, setInstructorFilters] = useState({
    studentId: 'all',
    courseId: 'all',
    classroom: 'all',
  });
  const [learningPaths, setLearningPaths] = useState([]);
  const [selectedLearningPathId, setSelectedLearningPathId] = useState(null);
  const [learningPathForm, setLearningPathForm] = useState({
    name: '',
    description: '',
    audience: '',
    courseIds: [],
  });
  const [editingLearningPathId, setEditingLearningPathId] = useState(null);
  const [materialUploadStatus, setMaterialUploadStatus] = useState({
    visible: false,
    progress: 0,
    fileName: '',
  });
  const localAssetPreviewUrlsRef = useRef({ cover: '', certificateLogo: '' });
  const [localAssetPreviewUrls, setLocalAssetPreviewUrls] = useState({
    cover: '',
    certificateLogo: '',
  });

  const setLocalAssetPreviewUrl = (key, nextUrl = '') => {
    const previousUrl = localAssetPreviewUrlsRef.current[key];
    if (previousUrl && previousUrl !== nextUrl) {
      URL.revokeObjectURL(previousUrl);
    }
    localAssetPreviewUrlsRef.current = {
      ...localAssetPreviewUrlsRef.current,
      [key]: nextUrl,
    };
    setLocalAssetPreviewUrls((prev) => ({ ...prev, [key]: nextUrl }));
  };

  const clearLocalAssetPreviewUrls = () => {
    Object.values(localAssetPreviewUrlsRef.current).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
    localAssetPreviewUrlsRef.current = { cover: '', certificateLogo: '' };
    setLocalAssetPreviewUrls({ cover: '', certificateLogo: '' });
  };

  const selectedCourse = courses.find((course) => course.id === selectedCourseId) || null;
  const selectedCourseProgress = selectedCourseId ? progressByCourse[selectedCourseId] : null;
  const unlockedLessonIds = useMemo(() => {
    if (!selectedCourse) return new Set();
    const orderedLessonIds = (selectedCourse.modules || [])
      .flatMap((module) => module.lessons || [])
      .map((lesson) => lesson.id);
    const completedIds = new Set(selectedCourseProgress?.enrollment?.completedLessonIds || []);
    const unlocked = new Set();
    let canUnlockNext = true;
    orderedLessonIds.forEach((lessonId) => {
      if (canUnlockNext) unlocked.add(lessonId);
      if (!completedIds.has(lessonId)) canUnlockNext = false;
    });
    return unlocked;
  }, [selectedCourse, selectedCourseProgress]);
  const orderedLessons = useMemo(
    () =>
      (selectedCourse?.modules || []).flatMap((module) =>
        (module.lessons || []).map((lesson) => ({ ...lesson, moduleTitle: module.title, moduleOrder: module.order })),
      ),
    [selectedCourse],
  );
  const currentLesson = useMemo(
    () => orderedLessons.find((lesson) => lesson.id === selectedLessonId) || orderedLessons[0] || null,
    [orderedLessons, selectedLessonId],
  );
  const currentLessonIndex = useMemo(
    () => orderedLessons.findIndex((lesson) => lesson.id === currentLesson?.id),
    [orderedLessons, currentLesson],
  );
  const previousLesson = currentLessonIndex > 0 ? orderedLessons[currentLessonIndex - 1] : null;
  const nextLesson =
    currentLessonIndex >= 0 && currentLessonIndex < orderedLessons.length - 1
      ? orderedLessons[currentLessonIndex + 1]
      : null;
  const creatorCourses = useMemo(() => {
    if (!canCreate) return [];
    return courses.filter((course) => userIsAdmin || Number(course.creatorId) === Number(user?.id));
  }, [courses, canCreate, userIsAdmin, user?.id]);
  const filteredCatalogCourses = useMemo(
    () =>
      filterAndSortCourses(courses, {
        query: courseSearchTerm,
        enrollmentFilter: catalogFilter,
        enrolledCourseIds,
        sort: catalogSort,
      }),
    [courses, courseSearchTerm, catalogFilter, enrolledCourseIds, catalogSort],
  );
  const catalogPagination = useMemo(
    () => paginateCourseCatalog(filteredCatalogCourses, catalogPage, COURSE_CATALOG_PAGE_SIZE),
    [filteredCatalogCourses, catalogPage],
  );
  const enrolledCourses = useMemo(
    () => courses.filter((course) => enrolledCourseIds.includes(course.id)),
    [courses, enrolledCourseIds],
  );
  const filteredStudentUsers = useMemo(() => {
    const normalizedTerm = studentSearchTerm.trim().toLowerCase();
    if (!normalizedTerm) return studentUsers;
    return studentUsers.filter((entry) => {
      const name = String(entry?.name || '').toLowerCase();
      const email = String(entry?.email || '').toLowerCase();
      return name.includes(normalizedTerm) || email.includes(normalizedTerm);
    });
  }, [studentUsers, studentSearchTerm]);
  const selectedAttendanceClassroom = useMemo(
    () => attendanceClassrooms.find((entry) => Number(entry.id) === Number(attendanceClassroomId)) || null,
    [attendanceClassrooms, attendanceClassroomId],
  );
  const selectedDiary = useMemo(
    () => classDiaries.find((entry) => Number(entry.id) === Number(selectedDiaryId)) || null,
    [classDiaries, selectedDiaryId],
  );
  const filteredAttendanceRecords = useMemo(
    () =>
      attendanceRecords.filter((entry) => {
        if (appliedAttendanceFilters.classroomId !== 'all' && Number(appliedAttendanceFilters.classroomId) !== Number(entry.classroomId)) return false;
        if (appliedAttendanceFilters.studentId !== 'all' && Number(appliedAttendanceFilters.studentId) !== Number(entry.studentId)) return false;
        if (appliedAttendanceFilters.teacherId !== 'all' && Number(appliedAttendanceFilters.teacherId) !== Number(entry.teacherId)) return false;
        return true;
      }),
    [attendanceRecords, appliedAttendanceFilters],
  );
  const filteredDiaryResults = useMemo(
    () =>
      classDiaries.filter((diary) => {
        if (diaryQueryFilters.startDate && String(diary.diaryDate || '') < diaryQueryFilters.startDate) return false;
        if (diaryQueryFilters.endDate && String(diary.diaryDate || '') > diaryQueryFilters.endDate) return false;
        if (diaryQueryFilters.teacherId !== 'all' && Number(diaryQueryFilters.teacherId) !== Number(diary.teacherId)) return false;
        if (diaryQueryFilters.theme.trim()) {
          const diaryThemeText = String(diary.theme || '').toLowerCase();
          if (!diaryThemeText.includes(diaryQueryFilters.theme.trim().toLowerCase())) return false;
        }
        return true;
      }),
    [classDiaries, diaryQueryFilters],
  );
  const selectedAttendanceStudent = useMemo(
    () => studentUsers.find((entry) => Number(entry.id) === Number(selectedAttendanceStudentId)) || null,
    [studentUsers, selectedAttendanceStudentId],
  );
  const selectedAttendanceRow = useMemo(
    () => attendanceRows.find((entry) => Number(entry.studentId) === Number(selectedAttendanceStudentId)) || null,
    [attendanceRows, selectedAttendanceStudentId],
  );

  useEffect(() => {
    if (activeTab !== TABS.ENROLLED) {
      setIsLearningOverlayOpen(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!message) return undefined;
    const timeoutId = window.setTimeout(() => setMessage(''), 6000);
    return () => window.clearTimeout(timeoutId);
  }, [message]);

  useEffect(() => {
    if (!isLearningOverlayOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsLearningOverlayOpen(false);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLearningOverlayOpen]);
  const filteredInstructorSubmissions = useMemo(
    () =>
      instructorSubmissions.filter((entry) => {
        const byStudent =
          instructorFilters.studentId === 'all' || Number(instructorFilters.studentId) === Number(entry.studentId);
        const byCourse =
          instructorFilters.courseId === 'all' || Number(instructorFilters.courseId) === Number(entry.courseId);
        const byClassroom =
          instructorFilters.classroom === 'all' || (entry.classroomNames || []).includes(instructorFilters.classroom);
        return byStudent && byCourse && byClassroom;
      }),
    [instructorSubmissions, instructorFilters],
  );
  const canManageLearningPaths = userIsAdmin || hasPermission(user, COURSE_EDITOR_VIEW_PERMISSION);
  const selectedLearningPath = useMemo(
    () => learningPaths.find((path) => path.id === selectedLearningPathId) || learningPaths[0] || null,
    [learningPaths, selectedLearningPathId],
  );
  const dashboardHighlights = useMemo(
    () => [
      {
        label: 'Cursos no catálogo',
        value: courses.length,
      },
      {
        label: 'Matrículas ativas',
        value: enrolledCourseIds.length,
      },
      {
        label: 'Turmas gerenciadas',
        value: classrooms.length,
      },
      {
        label: 'Atividades pendentes',
        value: instructorSubmissions.length,
      },
    ],
    [courses.length, enrolledCourseIds.length, classrooms.length, instructorSubmissions.length],
  );

  const loadCourses = useCallback(async () => {
    setLoading(true);
    try {
      const [data, enrollments] = await Promise.all([
        CourseService.list(),
        CourseService.listMyEnrollments(),
      ]);
      const safeCourses = Array.isArray(data) ? data : [];
      setCourses(safeCourses);
      setEnrolledCourseIds(Array.isArray(enrollments) ? enrollments : []);
      setSelectedCourseId((currentId) => currentId || safeCourses[0]?.id || null);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Falha ao carregar cursos.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCourseProgress = async (courseId) => {
    if (!courseId) return;
    try {
      const response = await CourseService.progress(courseId);
      setProgressByCourse((prev) => ({
        ...prev,
        [courseId]: response,
      }));
    } catch {
      // silêncio: progresso pode falhar para não matriculados
    }
  };

  const loadCoordinatorData = useCallback(async () => {
    if (!canCoordinate) return;
    try {
      const [usersResponse, classroomsResponse] = await Promise.all([
        api.get('/courses/admin/users'),
        api.get('/courses/classrooms'),
      ]);
      setUsers(usersResponse.data?.allUsers || usersResponse.data?.data || []);
      setStudentUsers(usersResponse.data?.data || []);
      setClassroomForm((prev) => ({
        ...prev,
        studentIds: prev.studentIds.filter((id) => (usersResponse.data?.data || []).some((entry) => entry.id === id)),
      }));
      setGrantForm((prev) => ({
        ...prev,
        userId: (usersResponse.data?.data || []).some((entry) => entry.id === prev.userId) ? prev.userId : null,
      }));
      setClassrooms(classroomsResponse.data?.data || []);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Falha ao carregar dados de coordenação.');
    }
  }, [canCoordinate]);

  const loadAttendanceData = useCallback(async () => {
    if (!canAccessClassDiary) return;
    try {
      const [classroomsData, recordsData, usersResponse, diariesData] = await Promise.all([
        CourseService.listAttendanceClassrooms(),
        CourseService.listAttendance({}),
        api.get('/courses/admin/users'),
        CourseService.listClassDiaries(),
      ]);
      const activeClassrooms = Array.isArray(classroomsData) ? classroomsData : [];
      setAttendanceClassrooms(activeClassrooms);
      setUsers(usersResponse.data?.allUsers || usersResponse.data?.data || []);
      setStudentUsers(usersResponse.data?.data || []);
      setClassDiaries(Array.isArray(diariesData) ? diariesData : []);
      setAttendanceClassroomId((currentId) => currentId || activeClassrooms[0]?.id || null);
      setSelectedDiaryId((currentId) =>
        currentId || (Array.isArray(diariesData) ? diariesData[0]?.id : null) || null,
      );
      setAttendanceRecords(Array.isArray(recordsData) ? recordsData : []);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Falha ao carregar diário de classe.');
    }
  }, [canAccessClassDiary]);

  const saveAttendance = async () => {
    if (!selectedDiaryId) {
      setMessage('Selecione ou crie um diário de classe antes de registrar frequência.');
      return;
    }
    if (!attendanceClassroomId || !canEditAttendance) {
      setMessage('Somente usuários com permissão de professor podem registrar frequência.');
      return;
    }
    if (attendanceRows.length === 0) {
      setMessage('Selecione uma turma com alunos para registrar frequência.');
      return;
    }
    setAttendanceSaving(true);
    try {
      await CourseService.recordAttendance(attendanceClassroomId, {
        diaryId: selectedDiaryId,
        attendanceDate,
        records: attendanceRows.map((row) => ({
          studentId: row.studentId,
          status: row.status || 'PRESENTE',
          lessonTopic: row.lessonTopic || diaryTheme || '',
          objective: row.objective || diaryObjective || '',
          allDay: row.allDay !== false,
          delayHours: row.allDay ? 0 : Number(row.delayHours || 0),
          delayMinutes: row.allDay ? 0 : Number(row.delayMinutes || 0),
          studentObservation: row.studentObservation || '',
          psychologistObservation: Boolean(row.psychologistObservation),
          pcd: Boolean(row.pcd),
        })),
      });
      setMessage('Diário de classe salvo com sucesso.');
      await loadAttendanceData();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível salvar o diário de classe.');
    } finally {
      setAttendanceSaving(false);
    }
  };

  const saveClassDiary = async () => {
    if (!attendanceClassroomId) {
      setMessage('Selecione a turma para criar o diário de classe.');
      return;
    }
    try {
      if (editingDiaryId) {
        await CourseService.updateClassDiary(editingDiaryId, {
          diaryDate: attendanceDate,
          theme: diaryTheme,
          objective: diaryObjective,
        });
        setMessage('Diário de classe atualizado com sucesso.');
      } else {
        const created = await CourseService.createClassDiary({
          classroomId: attendanceClassroomId,
          diaryDate: attendanceDate,
          theme: diaryTheme,
          objective: diaryObjective,
        });
        if (created?.id) setSelectedDiaryId(created.id);
        setMessage('Diário de classe criado com sucesso.');
      }
      setEditingDiaryId(null);
      await loadAttendanceData();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível salvar o diário de classe.');
    }
  };

  const startEditDiary = (diary) => {
    setEditingDiaryId(diary.id);
    setSelectedDiaryId(diary.id);
    setDiaryTheme(diary.theme || '');
    setDiaryObjective(diary.objective || '');
    setAttendanceDate(diary.diaryDate || new Date().toISOString().slice(0, 10));
    setAttendanceClassroomId(diary.classroomId || null);
  };

  const removeDiary = async (diaryId) => {
    const diary = classDiaries.find((entry) => Number(entry.id) === Number(diaryId));
    const confirmed = window.confirm(
      `Deseja excluir o diário${diary?.theme ? ` "${diary.theme}"` : ''}? Esta ação não pode ser desfeita.`,
    );
    if (!confirmed) return;
    try {
      await CourseService.deleteClassDiary(diaryId);
      if (selectedDiaryId === diaryId) {
        setSelectedDiaryId(null);
        setIsRegisteringFrequency(false);
      }
      setMessage('Diário de classe excluído com sucesso.');
      await loadAttendanceData();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível excluir o diário de classe.');
    }
  };

  const exportAttendanceXls = () => {
    if (!filteredAttendanceRecords.length) {
      setMessage('Não há frequência para exportar com os filtros selecionados.');
      return;
    }
    setAttendanceExporting(true);
    try {
      const rows = filteredAttendanceRecords.map((entry) => ({
        Data: formatCourseDate(entry.attendanceDate),
        Turma: attendanceClassrooms.find((classroom) => classroom.id === entry.classroomId)?.name || `Turma #${entry.classroomId}`,
        Aluno: studentUsers.find((student) => student.id === entry.studentId)?.name || `Aluno #${entry.studentId}`,
        Professor: users.find((teacher) => teacher.id === entry.teacherId)?.name || `Professor #${entry.teacherId}`,
        Situação: formatAttendanceStatus(entry.status),
      }));
      const worksheet = XLSXUtils.json_to_sheet(rows);
      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Frequencia');
      writeXLSXFile(workbook, `frequencia-diario-classe-${new Date().toISOString().slice(0, 10)}.xlsx`, { bookType: 'xlsx' });
    } catch (error) {
      setMessage('Não foi possível gerar o arquivo XLSX.');
    } finally {
      setAttendanceExporting(false);
    }
  };

  const exportAttendancePdf = async () => {
    if (!filteredAttendanceRecords.length) {
      setMessage('Não há frequência para exportar com os filtros selecionados.');
      return;
    }
    setAttendanceExporting(true);
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const margin = 12;
      const pageHeight = doc.internal.pageSize.getHeight();
      const colWidths = [18, 97, 24, 25, 14, 18, 57];
      const headers = ['Código', 'Nome', 'Data', 'Status', 'Psic.', 'Porc.', 'Observação'];
      const rowHeight = 6;
      let cursorY = 14;

      const records = [...filteredAttendanceRecords]
        .sort((a, b) => `${a.classroomId}-${a.attendanceDate}`.localeCompare(`${b.classroomId}-${b.attendanceDate}`))
        .map((entry) => {
          const classroom = attendanceClassrooms.find((item) => item.id === entry.classroomId);
          const student = studentUsers.find((item) => item.id === entry.studentId);
          const teacher = users.find((item) => item.id === entry.teacherId);
          const percentage = formatAttendanceStatus(entry.status) === 'Presente' ? '100,0%' : '0,0%';
          return {
            classroomId: entry.classroomId,
            classroomName: classroom?.name || `Turma #${entry.classroomId}`,
            date: entry.attendanceDate,
            studentCode: entry.studentId,
            studentName: student?.name || `Aluno #${entry.studentId}`,
            teacherName: teacher?.name || '-',
            status: formatAttendanceStatus(entry.status),
            psych: entry.psychologistObservation ? 'Sim' : 'Não',
            percentage,
            note: entry.studentObservation || '',
          };
        });

      const groups = records.reduce((acc, item) => {
        const key = `${item.classroomId}__${item.date}`;
        if (!acc[key]) acc[key] = { ...item, items: [] };
        acc[key].items.push(item);
        return acc;
      }, {});

      const ensureSpace = (neededHeight) => {
        if (cursorY + neededHeight <= pageHeight - margin) return;
        doc.addPage();
        cursorY = 14;
      };

      Object.values(groups).forEach((group, groupIndex) => {
        ensureSpace(28);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(`Turma: ${group.classroomName}`, margin, cursorY);
        cursorY += 6;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(`Data: ${formatCourseDate(group.date)}`, margin, cursorY);
        cursorY += 5;
        doc.text(`Tutor: ${group.teacherName}`, margin, cursorY);
        cursorY += 5;
        doc.text('Tema da aula: -', margin, cursorY);
        cursorY += 5;
        doc.text('Objetivo da atividade: -', margin, cursorY);
        cursorY += 7;

        let x = margin;
        doc.setFont('helvetica', 'bold');
        headers.forEach((header, idx) => {
          doc.rect(x, cursorY, colWidths[idx], rowHeight);
          doc.text(header, x + 1, cursorY + 4.2);
          x += colWidths[idx];
        });
        cursorY += rowHeight;

        doc.setFont('helvetica', 'normal');
        group.items.forEach((item) => {
          ensureSpace(rowHeight + 8);
          let rowX = margin;
          const rowData = [item.studentCode, item.studentName, formatCourseDate(item.date), item.status, item.psych, item.percentage, item.note];
          rowData.forEach((value, idx) => {
            doc.rect(rowX, cursorY, colWidths[idx], rowHeight);
            if (idx === 3) {
              const status = String(value).toLowerCase();
              if (status === 'presente') doc.setTextColor(0, 128, 0);
              if (status === 'ausente') doc.setTextColor(200, 0, 0);
              if (status === 'justificado') doc.setTextColor(37, 99, 235);
            }
            doc.text(String(value || '-'), rowX + 1, cursorY + 4.2, { maxWidth: colWidths[idx] - 2 });
            doc.setTextColor(0, 0, 0);
            rowX += colWidths[idx];
          });
          cursorY += rowHeight;
        });

        const attendanceTotals = countAttendanceStatuses(group.items);
        cursorY += 4;
        doc.setFont('helvetica', 'bold');
        doc.text(`Totais — Presente: ${attendanceTotals.present}, Ausente: ${attendanceTotals.absent}, Justificado: ${attendanceTotals.justified} / Registros neste dia: ${group.items.length}`, margin, cursorY);
        cursorY += 8;
        if (groupIndex < Object.values(groups).length - 1) {
          doc.line(margin, cursorY, doc.internal.pageSize.getWidth() - margin, cursorY);
          cursorY += 6;
        }
      });

      doc.save(`frequencia-diario-classe-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      setMessage('Não foi possível gerar o relatório em PDF.');
    } finally {
      setAttendanceExporting(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    setCatalogPage(1);
  }, [courseSearchTerm, catalogFilter, catalogSort]);

  useEffect(() => {
    if (catalogPage > catalogPagination.totalPages) {
      setCatalogPage(catalogPagination.totalPages);
    }
  }, [catalogPage, catalogPagination.totalPages]);

  useEffect(() => () => {
    Object.values(localAssetPreviewUrlsRef.current).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('courses.learningPaths.v1');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setLearningPaths(parsed);
        if (parsed[0]?.id) setSelectedLearningPathId(parsed[0].id);
      }
    } catch {
      // silêncio: sem trilhas salvas localmente
    }
  }, []);

  useEffect(() => {
    if (!learningPaths.length) {
      setSelectedLearningPathId(null);
      return;
    }
    if (!learningPaths.some((path) => path.id === selectedLearningPathId)) {
      setSelectedLearningPathId(learningPaths[0].id);
    }
  }, [learningPaths, selectedLearningPathId]);

  useEffect(() => {
    localStorage.setItem('courses.learningPaths.v1', JSON.stringify(learningPaths));
  }, [learningPaths]);

  useEffect(() => {
    loadCoordinatorData();
  }, [loadCoordinatorData]);

  useEffect(() => {
    loadAttendanceData();
  }, [loadAttendanceData]);

  useEffect(() => {
    if (!selectedDiary) return;
    setAttendanceClassroomId(selectedDiary.classroomId);
    setAttendanceDate(selectedDiary.diaryDate);
    setDiaryTheme(selectedDiary.theme || '');
    setDiaryObjective(selectedDiary.objective || '');
  }, [selectedDiary]);

  useEffect(() => {
    if (!selectedCourseId) return;
    loadCourseProgress(selectedCourseId);
  }, [selectedCourseId]);

  useEffect(() => {
    if (!orderedLessons.length) {
      setSelectedLessonId(null);
      return;
    }
    const firstUnlocked = orderedLessons.find((lesson) => unlockedLessonIds.has(lesson.id)) || orderedLessons[0];
    if (!selectedLessonId || !orderedLessons.some((lesson) => lesson.id === selectedLessonId)) {
      setSelectedLessonId(firstUnlocked.id);
    }
  }, [orderedLessons, unlockedLessonIds, selectedLessonId]);

  useEffect(() => {
    setActiveTab(TABS.ENROLLED);
  }, []);

  useEffect(() => {
    const loadInstructorSubmissions = async () => {
      if (!canReviewActivities) return;
      try {
        const data = await CourseService.listInstructorSubmissions();
        setInstructorSubmissions(Array.isArray(data) ? data : []);
      } catch (error) {
        setMessage(error?.response?.data?.error || 'Não foi possível carregar atividades recebidas.');
      }
    };
    loadInstructorSubmissions();
  }, [canReviewActivities]);

  useEffect(() => {
    if (!selectedAttendanceClassroom) {
      setAttendanceRows([]);
      setSelectedAttendanceStudentId(null);
      return;
    }
    const rows = (selectedAttendanceClassroom.studentIds || []).map((studentId) => {
      const existing = attendanceRecords.find(
        (entry) =>
          Number(entry.classroomId) === Number(selectedAttendanceClassroom.id) &&
          Number(entry.studentId) === Number(studentId) &&
          entry.attendanceDate === attendanceDate &&
          (!selectedDiaryId || Number(entry.diaryId) === Number(selectedDiaryId)),
      );
      return {
        studentId,
        status: existing?.status || 'PRESENTE',
        lessonTopic: existing?.lessonTopic || selectedDiary?.theme || '',
        objective: existing?.objective || selectedDiary?.objective || '',
        allDay: existing?.allDay === undefined ? true : Boolean(existing?.allDay),
        delayHours: Number(existing?.delayHours || 0),
        delayMinutes: Number(existing?.delayMinutes || 0),
        studentObservation: existing?.studentObservation || '',
        psychologistObservation: Boolean(existing?.psychologistObservation),
        pcd: Boolean(existing?.pcd),
      };
    });
    setAttendanceRows(rows);
  }, [selectedAttendanceClassroom, attendanceRecords, attendanceDate, selectedDiary, selectedDiaryId]);

  useEffect(() => {
    if (!attendanceRows.length) {
      setSelectedAttendanceStudentId(null);
      return;
    }
    if (!attendanceRows.some((entry) => Number(entry.studentId) === Number(selectedAttendanceStudentId))) {
      setSelectedAttendanceStudentId(attendanceRows[0].studentId);
    }
  }, [attendanceRows, selectedAttendanceStudentId]);

  const setAttendanceStatusForStudent = (studentId, status) => {
    setAttendanceRows((prev) =>
      prev.map((entry) =>
        Number(entry.studentId) === Number(studentId)
          ? { ...entry, status }
          : entry,
      ),
    );
  };
  const updateAttendanceFieldForStudent = (studentId, field, value) => {
    setAttendanceRows((prev) =>
      prev.map((entry) =>
        Number(entry.studentId) === Number(studentId)
          ? { ...entry, [field]: value }
          : entry,
      ),
    );
  };

  const updateModule = (index, nextModule) => {
    setForm((prev) => {
      const modules = [...prev.modules];
      modules[index] = nextModule;
      return { ...prev, modules };
    });
  };

  const handleUploadMaterial = async (moduleIndex, lessonIndex, file) => {
    const body = new FormData();
    body.append('file', file);
    setMaterialUploadStatus({
      visible: true,
      progress: 0,
      fileName: file.name || 'arquivo',
    });
    let uploadedSuccessfully = false;
    try {
      const response = await api.post('/upload', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent?.total) return;
          const computedProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setMaterialUploadStatus((prev) => ({
            ...prev,
            progress: Math.max(prev.progress, computedProgress),
          }));
        },
      });

      const material = {
        name: response.data?.name || file.name,
        type: fileTypeLabel(file.type),
        url: preferredStoredUploadReference(response.data),
        downloadUrl: response.data?.downloadUrl,
        relativePath: response.data?.relativePath,
        downloadPath: response.data?.downloadPath,
        mimeType: file.type,
        size: file.size,
      };

      setForm((prev) => {
        const modules = [...prev.modules];
        const lesson = { ...modules[moduleIndex].lessons[lessonIndex] };
        lesson.materials = [...lesson.materials, material];
        modules[moduleIndex] = {
          ...modules[moduleIndex],
          lessons: modules[moduleIndex].lessons.map((item, idx) =>
            idx === lessonIndex ? lesson : item,
          ),
        };
        return { ...prev, modules };
      });
      uploadedSuccessfully = true;
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível enviar o material da aula.');
    } finally {
      if (uploadedSuccessfully) {
        setMaterialUploadStatus((prev) => ({ ...prev, progress: 100 }));
        setTimeout(() => {
          setMaterialUploadStatus({
            visible: false,
            progress: 0,
            fileName: '',
          });
        }, 250);
      } else {
        setMaterialUploadStatus({
          visible: false,
          progress: 0,
          fileName: '',
        });
      }
    }
  };

  const handleAddVideoLinkMaterial = (moduleIndex, lessonIndex) => {
    const link = window.prompt('Cole o link do vídeo hospedado externamente para esta aula:');
    if (!link) return;
    const normalizedLink = link.trim();
    if (!/^https?:\/\//i.test(normalizedLink)) {
      setMessage('Link inválido. Informe uma URL completa iniciando com http:// ou https://.');
      return;
    }
    const titleInput = window.prompt('Nome do vídeo (opcional):');
    const videoId = getYouTubeVideoId(normalizedLink);
    if (isYouTubeHost(normalizedLink) && !videoId) {
      setMessage('Link do YouTube inválido para aula. Cole o link direto do vídeo (ex.: https://www.youtube.com/watch?v=ID).');
      return;
    }
    const material = {
      name: String(titleInput || '').trim() || 'Vídeo por link',
      type: 'video',
      url: normalizedLink,
      downloadUrl: normalizedLink,
      relativePath: null,
      downloadPath: null,
      mimeType: videoId ? 'video/youtube' : 'video/external-link',
      size: null,
      addedAt: new Date().toISOString(),
    };
    setForm((prev) => {
      const modules = [...prev.modules];
      const lesson = { ...modules[moduleIndex].lessons[lessonIndex] };
      lesson.materials = [...(lesson.materials || []), material];
      modules[moduleIndex] = {
        ...modules[moduleIndex],
        lessons: modules[moduleIndex].lessons.map((item, idx) =>
          idx === lessonIndex ? lesson : item,
        ),
      };
      return { ...prev, modules };
    });
  };

  const resetCreatorForm = () => {
    clearLocalAssetPreviewUrls();
    setEditingCourseId(null);
    setForm(createEmptyCourseForm());
    setCreatorPanel('overview');
    setExpandedCreatorModules({ 0: true });
    setExpandedCreatorLessons({ '0:0': true });
  };

  const startEditCourse = (course) => {
    clearLocalAssetPreviewUrls();
    setEditingCourseId(course.id);
    setForm(cloneCourseToForm(course));
    setCreatorPanel('overview');
    setExpandedCreatorModules({ 0: true });
    setExpandedCreatorLessons({ '0:0': true });
    setActiveTab(TABS.CREATOR);
  };

  const saveCourse = async () => {
    if (!canCreate) {
      setMessage('Você possui apenas visualização da edição de cursos.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const computedDurationMinutes = getFormDurationMinutes(form);
      const payload = {
        ...form,
        estimatedHours: Math.max(1, Math.round(computedDurationMinutes / 60)),
      };
      if (!String(form?.certificateConfig?.signerName || '').trim()) {
        setMessage('Informe o nome de quem assina o certificado na edição do curso.');
        setSaving(false);
        return;
      }
      if (editingCourseId) {
        await CourseService.update(editingCourseId, payload);
        setMessage('Curso atualizado com sucesso.');
      } else {
        await CourseService.create(payload);
        setMessage('Curso criado com sucesso.');
      }
      resetCreatorForm();
      await loadCourses();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível salvar o curso.');
    } finally {
      setSaving(false);
    }
  };

  const publishCourse = async (courseId) => {
    const actionId = `publish-${courseId}`;
    setCourseActionId(actionId);
    try {
      await CourseService.publish(courseId);
      setMessage('Curso publicado com sucesso.');
      await loadCourses();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível publicar o curso.');
    } finally {
      setCourseActionId('');
    }
  };

  const enrollCourse = async (courseId) => {
    const actionId = `enroll-${courseId}`;
    setCourseActionId(actionId);
    try {
      await CourseService.enroll(courseId);
      setMessage('Matrícula realizada com sucesso.');
      const enrollments = await CourseService.listMyEnrollments();
      setEnrolledCourseIds(Array.isArray(enrollments) ? enrollments : []);
      setSelectedCourseId(courseId);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível realizar a matrícula.');
    } finally {
      setCourseActionId('');
    }
  };

  const unenrollCourse = async (courseId) => {
    const course = courses.find((entry) => Number(entry.id) === Number(courseId));
    const confirmed = window.confirm(
      `Deseja realmente cancelar sua matrícula${course?.title ? ` em "${course.title}"` : ''}?`,
    );
    if (!confirmed) return;
    const actionId = `unenroll-${courseId}`;
    setCourseActionId(actionId);
    try {
      await CourseService.unenroll(courseId);
      setMessage('Desmatrícula realizada com sucesso.');
      const enrollments = await CourseService.listMyEnrollments();
      setEnrolledCourseIds(Array.isArray(enrollments) ? enrollments : []);
      if (selectedCourseId === courseId) {
        setSelectedCourseId(null);
      }
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível desmatricular.');
    } finally {
      setCourseActionId('');
    }
  };

  const generateCertificate = async (course) => {
    const issued = await CourseService.issueCertificate(course.id);
    const cfg = course?.certificateConfig || {};
    const companyName = cfg.companyName || '';
    const cnpj = cfg.cnpj || '';
    const address = cfg.address || '';
    const contact = cfg.contact || '';
    const signerName = cfg.signerName || 'Assinatura não informada';
    let logoUrl = resolveAssetUrl(cfg.logoUrl || '');
    if (isProtectedCourseReference(cfg.logoUrl || '') && course?.id) {
      try {
        const logoResponse = await api.get(`/courses/${course.id}/certificate-logo/content`, { responseType: 'blob' });
        logoUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Erro ao carregar logo.'));
          reader.readAsDataURL(logoResponse.data);
        });
      } catch {
        logoUrl = '';
      }
    }
    const logoPosition = cfg.logoPosition || 'center';
    const logoAlign = logoPosition === 'left' ? 'left' : logoPosition === 'right' ? 'right' : 'center';
    const bodyText = String(cfg.bodyTextTemplate || '')
      .replaceAll('{{ALUNO}}', user?.name || 'Aluno')
      .replaceAll('{{CURSO}}', course?.title || '')
      .replaceAll('{{CARGA_HORARIA}}', String(course?.estimatedHours || 0));
    const content = `
      <html><head><meta charset="utf-8" /><title>Certificado</title></head>
      <body style="font-family:Arial;padding:40px;text-align:center;border:12px solid ${cfg.primaryColor || '#d4af37'}">
      <h1 style="font-size:52px;margin:0 0 10px 0">Certificado</h1>
      <h2 style="margin:0 0 20px 0">${companyName}</h2>
      <h2>Certificado de Conclusão</h2>
      <p>${bodyText}</p>
      <p>Emitido em ${new Date().toLocaleDateString('pt-BR')}.</p>
      <div style="margin:30px auto 10px auto;width:320px;border-top:1px solid #333;padding-top:8px">
        <strong>${signerName}</strong><br/><span>Responsável pela assinatura</span>
      </div>
      ${logoUrl ? `<div style="text-align:${logoAlign}"><img src="${logoUrl}" alt="Logo" style="max-height:70px;margin-top:16px"/></div>` : ''}
      <p style="margin-top:10px;font-size:12px">ID de referência do certificado: <strong>${issued.referenceId}</strong></p>
      <p style="margin-top:4px;font-size:12px">Consulta pública: <strong>${window.location.origin}/certificados/consulta</strong></p>
      <p style="margin-top:30px;font-size:13px">${companyName}${cnpj ? ` - CNPJ ${cnpj}` : ''}${address ? ` - ${address}` : ''}${contact ? ` - Contato: ${contact}` : ''}</p>
      </body></html>`;
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `certificado-${(course?.title || 'curso').replace(/\s+/g, '-').toLowerCase()}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const markLessonAsDone = async (courseId, lessonId) => {
    try {
      await CourseService.enroll(courseId);
      await CourseService.completeLesson(courseId, lessonId);
      setMessage('Aula concluída com sucesso.');
      await loadCourseProgress(courseId);
      const enrollments = await CourseService.listMyEnrollments();
      setEnrolledCourseIds(Array.isArray(enrollments) ? enrollments : []);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível concluir a aula.');
    }
  };

  const markLessonAsNotDone = async (courseId, lessonId) => {
    try {
      await CourseService.uncompleteLesson(courseId, lessonId);
      setMessage('Aula marcada como não concluída.');
      await loadCourseProgress(courseId);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível voltar a aula para não concluída.');
    }
  };

  const uploadCourseCover = async (file) => {
    const previewUrl = URL.createObjectURL(file);
    setLocalAssetPreviewUrl('cover', previewUrl);
    const body = new FormData();
    body.append('file', file);
    const response = await api.post('/upload', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setForm((prev) => ({ ...prev, coverImageUrl: preferredStoredUploadReference(response.data) }));
  };

  const uploadCertificateLogo = async (file) => {
    const previewUrl = URL.createObjectURL(file);
    setLocalAssetPreviewUrl('certificateLogo', previewUrl);
    const body = new FormData();
    body.append('file', file);
    const response = await api.post('/upload', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setForm((prev) => ({
      ...prev,
      certificateConfig: {
        ...(prev.certificateConfig || {}),
        logoUrl: preferredStoredUploadReference(response.data),
      },
    }));
  };

  const uploadAnswerAttachment = async (file) => {
    const body = new FormData();
    body.append('file', file);
    const response = await api.post('/upload', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return preferredStoredUploadReference(response.data);
  };

  const downloadTaskAttachment = async (entry) => {
    const endpoint = `/courses/${entry.courseId}/lessons/${entry.lessonId}/tasks/${entry.taskId}/students/${entry.studentId}/attachment`;
    try {
      const response = await api.get(endpoint, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = entry.attachmentName || `anexo-tarefa-${entry.taskId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível baixar o anexo enviado.');
    }
  };

  const updateTaskAnswer = (taskId, questionIndex, value) => {
    setTaskAnswers((prev) => {
      const current = prev[taskId] || [];
      const next = [...current];
      next[questionIndex] = value;
      return { ...prev, [taskId]: next };
    });
  };

  const updateQuizAnswer = (lessonId, questionIndex, value) => {
    setQuizAnswers((prev) => {
      const current = prev[lessonId] || [];
      const next = [...current];
      next[questionIndex] = value;
      return { ...prev, [lessonId]: next };
    });
  };

  const submitTaskForm = async (courseId, lessonId, task) => {
    try {
      const answers = taskAnswers[task.id] || [];
      const result = await CourseService.submitTask(courseId, lessonId, task.id, {
        answers,
        attachmentUrl: taskAttachments[task.id] || null,
      });
      setMessage(
        typeof result?.score === 'number'
          ? `Tarefa enviada. Nota automática: ${result.score}`
          : 'Tarefa enviada com sucesso.',
      );
      setTaskAttachments((prev) => ({ ...prev, [task.id]: null }));
      await loadCourseProgress(courseId);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível enviar a tarefa.');
    }
  };

  const submitQuizForm = async (courseId, lessonId) => {
    try {
      const answers = quizAnswers[lessonId] || [];
      const result = await CourseService.submitQuiz(courseId, lessonId, answers);
      setMessage(`Avaliação enviada. Nota: ${result?.score ?? 0}`);
      await loadCourseProgress(courseId);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível enviar a avaliação.');
    }
  };

  const toggleMultiSelect = (values, value) => {
    if (values.includes(value)) {
      return values.filter((entry) => entry !== value);
    }
    return [...values, value];
  };

  const createClassroom = async () => {
    try {
      if (editingClassroomId) {
        await api.put(`/courses/classrooms/${editingClassroomId}`, classroomForm);
        setMessage('Turma atualizada com sucesso.');
      } else {
        await api.post('/courses/classrooms', classroomForm);
        setMessage('Turma criada com sucesso.');
      }
      setClassroomForm({
        name: '',
        description: '',
        status: 'ACTIVE',
        coordinatorUserId: user?.id || null,
        studentIds: [],
        courseIds: [],
      });
      setEditingClassroomId(null);
      await loadCoordinatorData();
    } catch (error) {
      setMessage(error?.response?.data?.error || `Não foi possível ${editingClassroomId ? 'atualizar' : 'criar'} turma.`);
    }
  };

  const startEditClassroom = (classroom) => {
    setEditingClassroomId(classroom.id);
    setClassroomForm({
      name: classroom.name || '',
      description: classroom.description || '',
      status: classroom.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
      coordinatorUserId: classroom.coordinatorUserId || user?.id || null,
      studentIds: Array.isArray(classroom.studentIds) ? classroom.studentIds : [],
      courseIds: Array.isArray(classroom.courseIds) ? classroom.courseIds : [],
    });
  };

  const cancelEditClassroom = () => {
    setEditingClassroomId(null);
    setClassroomForm({
      name: '',
      description: '',
      status: 'ACTIVE',
      coordinatorUserId: user?.id || null,
      studentIds: [],
      courseIds: [],
    });
  };

  const deleteClassroom = async (classroom) => {
    const confirmed = window.confirm(`Deseja excluir a turma "${classroom.name}"?`);
    if (!confirmed) return;
    try {
      await api.delete(`/courses/classrooms/${classroom.id}`);
      if (editingClassroomId === classroom.id) {
        cancelEditClassroom();
      }
      setMessage('Turma excluída com sucesso.');
      await loadCoordinatorData();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível excluir turma.');
    }
  };

  const consultCertificate = async () => {
    try {
      const result = await CourseService.consultCertificate(certificateQuery.trim());
      setCertificateResult(result || null);
      setMessage('');
    } catch (error) {
      setCertificateResult(null);
      setMessage(error?.response?.data?.error || 'Não foi possível consultar certificado.');
    }
  };

  const grantDirectAccess = async () => {
    try {
      if (!grantForm.courseId || !grantForm.userId) {
        setMessage('Selecione curso e aluno para conceder acesso.');
        return;
      }
      await api.post(`/courses/${grantForm.courseId}/grants`, {
        userId: grantForm.userId,
        accessStartAt: grantForm.accessStartAt || null,
        accessEndAt: grantForm.accessEndAt || null,
      });
      setMessage('Acesso direto concedido com sucesso.');
      setGrantForm({ courseId: null, userId: null, accessStartAt: '', accessEndAt: '' });
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Não foi possível conceder acesso direto.');
    }
  };

  const saveLearningPath = () => {
    if (!learningPathForm.name.trim()) {
      setMessage('Informe um nome para a trilha de ensino.');
      return;
    }
    if (!learningPathForm.courseIds.length) {
      setMessage('Selecione ao menos um curso para compor a trilha.');
      return;
    }
    const nextPath = {
      id: editingLearningPathId || `path-${Date.now()}`,
      name: learningPathForm.name.trim(),
      description: learningPathForm.description.trim(),
      audience: learningPathForm.audience.trim(),
      courseIds: learningPathForm.courseIds,
    };
    setLearningPaths((prev) =>
      editingLearningPathId ? prev.map((path) => (path.id === editingLearningPathId ? nextPath : path)) : [...prev, nextPath],
    );
    setSelectedLearningPathId(nextPath.id);
    setEditingLearningPathId(null);
    setLearningPathForm({ name: '', description: '', audience: '', courseIds: [] });
    setMessage(editingLearningPathId ? 'Trilha de ensino atualizada com sucesso.' : 'Trilha de ensino criada com sucesso.');
  };

  const startEditLearningPath = (path) => {
    setEditingLearningPathId(path.id);
    setSelectedLearningPathId(path.id);
    setLearningPathForm({
      name: path.name || '',
      description: path.description || '',
      audience: path.audience || '',
      courseIds: Array.isArray(path.courseIds) ? [...path.courseIds] : [],
    });
  };

  const cancelLearningPathEdit = () => {
    setEditingLearningPathId(null);
    setLearningPathForm({ name: '', description: '', audience: '', courseIds: [] });
  };

  const deleteLearningPath = (pathId) => {
    setLearningPaths((prev) => prev.filter((path) => path.id !== pathId));
    if (editingLearningPathId === pathId) {
      cancelLearningPathEdit();
    }
    if (selectedLearningPathId === pathId) {
      setSelectedLearningPathId(null);
    }
    setMessage('Trilha de ensino removida com sucesso.');
  };

  const tabs = [
    {
      id: TABS.CATALOG,
      label: 'Catálogo do aluno',
      icon: FaUserGraduate,
      visible: true,
    },
    {
      id: TABS.ENROLLED,
      label: 'Cursos Matriculados',
      icon: FaBookOpen,
      visible: true,
    },
    {
      id: TABS.LEARNING_PATHS,
      label: 'Trilhas de Ensino',
      icon: FaProjectDiagram,
      visible: true,
    },
    {
      id: TABS.CREATOR,
      label: 'Editor de cursos',
      icon: FaChalkboardTeacher,
      visible: canViewEditor,
    },
    {
      id: TABS.COORDINATOR,
      label: 'Turmas e acessos',
      icon: FaUsersCog,
      visible: canCoordinate,
    },
    {
      id: TABS.TEACHER,
      label: 'Atividades recebidas',
      icon: FaListOl,
      visible: canReviewActivities,
    },
    {
      id: TABS.CERTIFICATE,
      label: 'Consulta de certificados',
      icon: FaBookOpen,
      visible: true,
    },
  ].filter((tab) => tab.visible);

  return (
    <div className="min-w-0 space-y-5 bg-white p-2 sm:p-3 md:p-5">
      <div className="overflow-hidden rounded-3xl border border-blue-100 bg-white shadow-sm">
        <div className="bg-gradient-to-r from-[#071f54] via-[#0b3474] to-[#0d4d91] px-4 py-6 sm:px-6 sm:py-7 md:px-8">
          <div className="flex items-start gap-3">
            <div className="rounded-2xl bg-white/10 p-3 text-blue-100 shadow-inner ring-1 ring-white/10">
              <FaBookOpen className="text-xl sm:text-2xl" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">Central Acadêmica</p>
              <div className="mt-1 flex items-center gap-2">
                <h1 className="text-xl font-semibold tracking-tight text-white sm:text-2xl md:text-3xl">Módulo de Cursos</h1>
                <FaInfoCircle className="cursor-help text-blue-200" title="Catálogo, matrículas, trilhas, autoria, turmas, atividades e certificados." />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 bg-white px-4 py-4 md:grid-cols-4 md:px-8">
          {dashboardHighlights.map((entry, index) => {
            const tones = [
              'border-blue-100 bg-blue-50/70 text-blue-700',
              'border-violet-100 bg-violet-50/70 text-violet-700',
              'border-emerald-100 bg-emerald-50/70 text-emerald-700',
              'border-amber-100 bg-amber-50/70 text-amber-700',
            ];
            return (
              <div
                key={entry.label}
                className={`rounded-2xl border p-4 shadow-sm ${tones[index % tones.length]}`}
              >
                <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{entry.label}</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{entry.value}</p>
              </div>
            );
          })}
        </div>
      </div>

      {message ? (
        <div role="status" aria-live="polite" className="fixed left-1/2 top-3 z-[10000] w-[calc(100%-1rem)] max-w-xl -translate-x-1/2 rounded-xl border border-blue-300 bg-white px-4 py-3 text-sm text-blue-900 shadow-xl sm:top-6 sm:w-[92%]">
          <div className="flex items-center justify-between gap-2">
            <span>{message}</span>
            <button type="button" className="rounded border px-2 py-0.5 text-xs" onClick={() => setMessage('')}>Fechar</button>
          </div>
        </div>
      ) : null}

      <nav aria-label="Áreas do módulo de cursos" className="sticky top-0 z-30 -mx-1 overflow-x-auto rounded-2xl border border-blue-100 bg-blue-50/95 p-2 shadow-sm backdrop-blur sm:mx-0">
        <div role="tablist" className="flex min-w-max gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                id={`course-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={`course-panel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-xl border px-3 py-2 text-sm font-medium transition sm:px-4 ${
                  active
                    ? 'border-[#0b3474] bg-[#0b3474] text-white shadow'
                    : 'border-transparent bg-white text-slate-700 hover:border-blue-200 hover:text-blue-700'
                }`}
              >
                <Icon aria-hidden="true" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </nav>

      {activeTab === TABS.CATALOG ? (
        <section
          id={`course-panel-${TABS.CATALOG}`}
          role="tabpanel"
          aria-labelledby={`course-tab-${TABS.CATALOG}`}
          className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5"
        >
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                <FaBookOpen className="text-blue-600" /> Catálogo de cursos
                <FaInfoCircle className="cursor-help text-slate-400" title="Busque e filtre cursos por nome, descrição, nível, situação e matrícula." />
              </h2>
            </div>
            <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_220px] lg:max-w-2xl">
              <label className="relative block">
                <span className="sr-only">Buscar cursos</span>
                <input
                  type="search"
                  value={courseSearchTerm}
                  onChange={(event) => setCourseSearchTerm(event.target.value)}
                  placeholder="Buscar no catálogo"
                  className="min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 pr-10 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
                {courseSearchTerm ? (
                  <button
                    type="button"
                    aria-label="Limpar busca de cursos"
                    onClick={() => setCourseSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100"
                  >
                    Limpar
                  </button>
                ) : null}
              </label>
              <select
                value={catalogSort}
                onChange={(event) => setCatalogSort(event.target.value)}
                aria-label="Ordenar catálogo"
                className="min-h-11 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              >
                <option value="title">Ordem alfabética</option>
                <option value="newest">Mais recentes</option>
                <option value="duration-asc">Menor duração</option>
                <option value="duration-desc">Maior duração</option>
              </select>
            </div>
          </div>

          <div className="mb-4 flex gap-2 overflow-x-auto pb-1" aria-label="Filtrar catálogo por matrícula">
            {[
              ['all', 'Todos'],
              ['enrolled', 'Matriculados'],
              ['not-enrolled', 'Não matriculados'],
            ].map(([filterId, label]) => (
              <button
                key={filterId}
                type="button"
                aria-pressed={catalogFilter === filterId}
                className={`min-h-10 whitespace-nowrap rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                  catalogFilter === filterId
                    ? 'border-blue-600 bg-[#0b3474] text-white shadow-sm'
                    : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-blue-200 hover:bg-white hover:text-blue-700'
                }`}
                onClick={() => setCatalogFilter(filterId)}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3" aria-label="Carregando cursos">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-72 animate-pulse rounded-2xl border border-slate-200 bg-slate-100" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                {catalogPagination.items.map((course) => {
                  const enrolled = enrolledCourseIds.includes(course.id);
                  const statusLabel = formatCourseStatus(course.status);
                  const actionBusy = courseActionId.endsWith(`-${course.id}`);
                  return (
                    <article key={course.id} className="flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md">
                      {course.coverImageUrl ? (
                        <CourseCover course={course} className="aspect-[16/9] w-full object-cover" alt={`Capa do curso ${course.title}`} />
                      ) : (
                        <div className="flex aspect-[16/9] items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 text-3xl text-blue-500">
                          <FaBookOpen aria-hidden="true" />
                        </div>
                      )}
                      <div className="flex flex-1 flex-col p-4">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                            statusLabel === 'Publicado'
                              ? 'bg-emerald-100 text-emerald-700'
                              : statusLabel === 'Rascunho'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-slate-100 text-slate-600'
                          }`}>
                            {statusLabel}
                          </span>
                          <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-semibold capitalize text-blue-700">
                            {course.difficulty || 'Nível não informado'}
                          </span>
                          {enrolled ? <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[10px] font-semibold text-indigo-700">Matriculado</span> : null}
                        </div>
                        <button
                          type="button"
                          className="line-clamp-2 text-left text-base font-semibold text-slate-900 hover:text-blue-700"
                          onClick={() => {
                            setSelectedCourseId(course.id);
                            setSelectedCourseDetailTab('trilha');
                          }}
                        >
                          {course.title}
                        </button>
                        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">{course.description || 'Descrição não informada.'}</p>
                        <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div className="rounded-lg bg-slate-50 p-2">
                            <dt className="font-semibold text-slate-500">Duração</dt>
                            <dd className="mt-0.5 font-medium text-slate-800">{formatCourseDuration(getCourseDurationMinutes(course))}</dd>
                          </div>
                          <div className="rounded-lg bg-slate-50 p-2">
                            <dt className="font-semibold text-slate-500">Acesso</dt>
                            <dd className="mt-0.5 font-medium text-slate-800">{course.requiresAccessGrant ? 'Com liberação' : 'Livre'}</dd>
                          </div>
                        </dl>
                        {(course.prerequisiteCourseIds || []).length > 0 ? (
                          <p className="mt-2 text-xs text-slate-500">Pré-requisitos: {(course.prerequisiteCourseIds || []).length} curso(s)</p>
                        ) : null}
                        <div className="mt-auto grid grid-cols-1 gap-2 pt-4 sm:grid-cols-2">
                          {enrolled ? (
                            <button
                              type="button"
                              className="min-h-10 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-wait disabled:opacity-60"
                              onClick={() => unenrollCourse(course.id)}
                              disabled={actionBusy}
                            >
                              {courseActionId === `unenroll-${course.id}` ? 'Cancelando...' : 'Desmatricular'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="min-h-10 rounded-xl bg-[#0b3474] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#0a2c62] disabled:cursor-wait disabled:opacity-60"
                              onClick={() => enrollCourse(course.id)}
                              disabled={actionBusy}
                            >
                              {courseActionId === `enroll-${course.id}` ? 'Matriculando...' : 'Matricular'}
                            </button>
                          )}
                          {canCreate ? (
                            <button
                              type="button"
                              className="inline-flex min-h-10 items-center justify-center gap-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
                              onClick={() => startEditCourse(course)}
                            >
                              <FaEdit aria-hidden="true" /> Editar
                            </button>
                          ) : null}
                          {canCreate && course.status !== 'published' ? (
                            <button
                              type="button"
                              className="min-h-10 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-60 sm:col-span-2"
                              onClick={() => publishCourse(course.id)}
                              disabled={actionBusy}
                            >
                              {courseActionId === `publish-${course.id}` ? 'Publicando...' : 'Publicar curso'}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
              {!catalogPagination.totalItems ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                  <FaBookOpen className="mx-auto text-2xl text-slate-400" />
                  <p className="mt-2 text-sm font-semibold text-slate-700">Nenhum curso encontrado</p>
                  <p className="mt-1 text-xs text-slate-500">Ajuste a busca ou o filtro de matrícula.</p>
                </div>
              ) : null}
              {catalogPagination.totalItems ? (
                <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-slate-600" aria-live="polite">
                    Exibindo {catalogPagination.from}–{catalogPagination.to} de {catalogPagination.totalItems} cursos
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:flex">
                    <button
                      type="button"
                      className="min-h-10 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
                      onClick={() => setCatalogPage((page) => Math.max(1, page - 1))}
                      disabled={catalogPagination.page <= 1}
                    >
                      Anterior
                    </button>
                    <span className="col-span-2 row-start-1 text-center text-xs font-semibold text-slate-600 sm:col-auto sm:self-center">
                      Página {catalogPagination.page} de {catalogPagination.totalPages}
                    </span>
                    <button
                      type="button"
                      className="min-h-10 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
                      onClick={() => setCatalogPage((page) => Math.min(catalogPagination.totalPages, page + 1))}
                      disabled={catalogPagination.page >= catalogPagination.totalPages}
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      {activeTab === TABS.ENROLLED ? (
        <section id={`course-panel-${TABS.ENROLLED}`} role="tabpanel" aria-labelledby={`course-tab-${TABS.ENROLLED}`} className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
            <FaListOl className="text-blue-600" /> Cursos Matriculados
            <FaInfoCircle className="text-slate-400" title="Acompanhe o progresso aula por aula e envie atividades." />
          </h2>
          <div className="space-y-4">
            <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 md:grid md:grid-cols-2 md:overflow-visible md:pb-0 xl:grid-cols-3">
              {enrolledCourses.map((course) => (
                <button
                  key={course.id}
                  type="button"
                  onClick={() => {
                    setSelectedCourseId(course.id);
                    setSelectedCourseDetailTab('trilha');
                    setSelectedLessonId(null);
                    setIsLearningOverlayOpen(true);
                  }}
                  className={`min-w-[260px] snap-start rounded-2xl border p-3 text-left transition sm:p-4 md:min-w-0 ${selectedCourseId === course.id ? 'border-blue-600 bg-gradient-to-br from-blue-50 to-white shadow' : 'border-slate-200 bg-white hover:border-blue-200'}`}
                >
                  {course.coverImageUrl ? (
                    <CourseCover course={course} className="mb-2 aspect-[16/9] w-full rounded-lg object-cover" alt={`Capa do curso ${course.title}`} />
                  ) : null}
                  <p className="font-semibold text-slate-800">{course.title}</p>
                  <p className="line-clamp-2 text-xs text-slate-500">{course.description}</p>
                </button>
              ))}
            </div>
            {!enrolledCourses.length ? (
              <p className="text-sm text-slate-500">Você ainda não está matriculado em cursos.</p>
            ) : null}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              {selectedCourse && enrolledCourseIds.includes(selectedCourse.id) ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Curso selecionado: {selectedCourse.title}</p>
                    <p className="text-xs text-slate-600">Toque em "Abrir modo aula" para estudar em tela sobreposta.</p>
                  </div>
                  <button
                    type="button"
                    className="min-h-11 rounded-xl bg-[#0b3474] px-4 py-2 text-sm font-semibold text-white"
                    onClick={() => setIsLearningOverlayOpen(true)}
                  >
                    Abrir modo aula
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Selecione um curso matriculado para abrir o modo aula.</p>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {isLearningOverlayOpen && selectedCourse && enrolledCourseIds.includes(selectedCourse.id) ? (
        <div className="fixed inset-0 z-50 bg-slate-950/70 p-0 backdrop-blur-sm sm:p-4">
          <div role="dialog" aria-modal="true" aria-labelledby="course-learning-dialog-title" className="flex h-full w-full flex-col overflow-hidden bg-white sm:mx-auto sm:max-h-[94vh] sm:max-w-6xl sm:rounded-3xl sm:shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p id="course-learning-dialog-title" className="truncate text-sm font-semibold text-slate-800 sm:text-base">Modo aula · {selectedCourse.title}</p>
                  <p className="text-xs text-slate-500">Experiência imersiva para assistir no celular e desktop.</p>
                </div>
                <button type="button" aria-label="Fechar modo aula" className="min-h-10 rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700" onClick={() => setIsLearningOverlayOpen(false)}>
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-5">
              <div className="rounded-2xl border border-slate-200 p-4">
                <h3 className="mb-2 flex items-center gap-2 font-semibold text-slate-800">
                  <FaListOl className="text-blue-600" /> Sequência: {selectedCourse.title}
                </h3>
                <div className="mb-3 mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full bg-[#0b3474] transition-all"
                    style={{ width: `${selectedCourseProgress?.progress?.percentage || 0}%` }}
                  />
                </div>
                <p className="mb-3 text-xs text-slate-600">
                  Progresso: {selectedCourseProgress?.progress?.completedLessons || 0}/
                  {selectedCourseProgress?.progress?.totalLessons || 0} aulas •{' '}
                  {selectedCourseProgress?.progress?.completedDurationMinutes || 0}/
                  {selectedCourseProgress?.progress?.totalDurationMinutes || getCourseDurationMinutes(selectedCourse)} min
                </p>
                {(selectedCourseProgress?.progress?.percentage || 0) >= 100 ? (
                  <div className="mb-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3">
                    <p className="text-sm font-semibold text-emerald-800">Parabéns! Você concluiu este curso com sucesso.</p>
                    <button
                      type="button"
                      className="mt-2 rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white"
                      onClick={() => generateCertificate(selectedCourse)}
                    >
                      Gerar certificado de conclusão
                    </button>
                  </div>
                ) : null}

                <div className="mb-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  <button
                    type="button"
                    className={`rounded-lg px-2 py-2 text-xs font-semibold ${selectedCourseDetailTab === 'trilha' ? 'bg-[#0b3474] text-white' : 'border bg-white'}`}
                    onClick={() => setSelectedCourseDetailTab('trilha')}
                  >
                    Trilha completa
                  </button>
                  <button
                    type="button"
                    className={`rounded-lg px-2 py-2 text-xs font-semibold ${selectedCourseDetailTab === 'concluidas' ? 'bg-[#0b3474] text-white' : 'border bg-white'}`}
                    onClick={() => setSelectedCourseDetailTab('concluidas')}
                  >
                    Aulas concluídas
                  </button>
                </div>

                {selectedCourseDetailTab === 'trilha' ? (
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Escolha rápida da aula</p>
                      <select
                        value={selectedLessonId || currentLesson?.id || ''}
                        onChange={(event) => setSelectedLessonId(event.target.value)}
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                      >
                        {orderedLessons.map((lesson, index) => {
                          const locked = !unlockedLessonIds.has(lesson.id);
                          return (
                            <option key={`lesson-selector-${lesson.id}`} value={lesson.id} disabled={locked}>
                              {`Aula ${index + 1} • ${lesson.title}${locked ? ' (bloqueada)' : ''}`}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {currentLesson ? (
                      <div className={`rounded-2xl border p-3 sm:p-4 ${unlockedLessonIds.has(currentLesson.id) ? 'border-slate-200 bg-slate-50' : 'border-amber-200 bg-amber-50'}`}>
                        <div className="mb-4 space-y-3 rounded-xl border border-slate-200 bg-white/90 p-3">
                          <div>
                            <p className="text-xs font-semibold text-slate-500">Módulo {currentLesson.moduleOrder}: {currentLesson.moduleTitle}</p>
                            <p className="text-base font-semibold text-slate-800 sm:text-lg">Aula {currentLessonIndex + 1} · {currentLesson.title}</p>
                            <p className="text-sm text-slate-600">{currentLesson.description}</p>
                          </div>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <button
                              type="button"
                              onClick={() => previousLesson && setSelectedLessonId(previousLesson.id)}
                              disabled={!previousLesson}
                              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                              aria-label="Aula anterior"
                            >
                              <FaChevronLeft />
                              <span>Aula anterior</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => nextLesson && unlockedLessonIds.has(nextLesson.id) && setSelectedLessonId(nextLesson.id)}
                              disabled={!nextLesson || !unlockedLessonIds.has(nextLesson.id)}
                              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-blue-600 bg-[#0b3474] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#0a2c62] disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300"
                              aria-label="Próxima aula"
                            >
                              <span>Próxima aula</span>
                              <FaChevronRight />
                            </button>
                          </div>
                        </div>
                        {!unlockedLessonIds.has(currentLesson.id) ? (
                          <p className="rounded-lg border border-amber-200 bg-amber-100 p-3 text-sm font-medium text-amber-800">Conclua a aula anterior para visualizar o conteúdo desta aula.</p>
                        ) : (
                          <>
                            <div className="space-y-3">
                              <div className="rounded-xl border border-slate-200 bg-white p-3">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Conteúdo da aula</p>
                                {(currentLesson.materials || []).length > 0 ? (
                                  <div className="space-y-3">
                                    {(currentLesson.materials || []).map((material) => (
                                      <CourseMaterialPreview key={`preview-${material.id || material.url || material.name}`} courseId={selectedCourse?.id} material={material} />
                                    ))}
                                  </div>
                                ) : (
                                  <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-600">
                                    Esta aula ainda não possui materiais anexados.
                                  </p>
                                )}
                              </div>

                              {(currentLesson.tasks || []).length ? (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                                  <p className="mb-2 text-sm font-semibold text-amber-800">Tarefas da aula</p>
                                  <div className="space-y-3">
                                    {(currentLesson.tasks || []).map((task) => (
                                      <div key={`task-${task.id}`} className="rounded-xl border border-amber-200 bg-white p-3">
                                        <p className="text-sm font-semibold text-amber-900">{task.title}</p>
                                        <p className="mt-1 text-xs text-amber-700">{task.instructions || task.description}</p>
                                        {(task.formQuestions || []).map((question, questionIndex) => (
                                          <div key={`task-q-${task.id}-${question.id || questionIndex}`} className="mt-3 text-xs">
                                            <p className="mb-1 font-semibold text-slate-700">Pergunta {questionIndex + 1}</p>
                                            <p className="mb-1 text-slate-600">{question.prompt}</p>
                                            {question.type === 'multiple_choice' ? (
                                              <select className="min-h-11 w-full rounded-lg border border-slate-300 p-2 text-sm" value={String((taskAnswers[task.id] || [])[questionIndex] ?? '')} onChange={(event) => updateTaskAnswer(task.id, questionIndex, Number(event.target.value))}>
                                                <option value="">Selecione</option>
                                                {(question.options || []).map((option, optionIndex) => <option key={`task-opt-${task.id}-${optionIndex}`} value={optionIndex}>{option}</option>)}
                                              </select>
                                            ) : null}
                                            {question.type === 'text' ? <textarea className="min-h-24 w-full rounded-lg border border-slate-300 p-2 text-sm" value={String((taskAnswers[task.id] || [])[questionIndex] ?? '')} onChange={(event) => updateTaskAnswer(task.id, questionIndex, event.target.value)} /> : null}
                                            {question.type === 'attachment' ? <input type="file" className="w-full rounded-lg border border-dashed border-slate-300 bg-white p-2 text-xs file:mr-3 file:rounded file:border-0 file:bg-amber-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-amber-800" onChange={async (event) => { const file = event.target.files?.[0]; if (!file) return; const url = await uploadAnswerAttachment(file); updateTaskAnswer(task.id, questionIndex, url); }} /> : null}
                                          </div>
                                        ))}
                                        <button type="button" className="mt-3 min-h-11 w-full rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white" onClick={() => submitTaskForm(selectedCourse.id, currentLesson.id, task)}>Enviar tarefa</button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}

                              {currentLesson.quiz ? (
                                <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
                                  <p className="mb-2 text-sm font-semibold text-purple-800">Avaliação: {currentLesson.quiz.title}</p>
                                  {(currentLesson.quiz.questions || []).map((question, questionIndex) => (
                                    <div key={`quiz-q-${currentLesson.id}-${question.id || questionIndex}`} className="mt-3 text-xs">
                                      <p className="mb-1 font-semibold text-slate-700">Questão {questionIndex + 1}</p>
                                      <p className="mb-1 text-slate-600">{question.prompt}</p>
                                      {question.type === 'multiple_choice' ? <select className="min-h-11 w-full rounded-lg border border-slate-300 p-2 text-sm" value={String((quizAnswers[currentLesson.id] || [])[questionIndex] ?? '')} onChange={(event) => updateQuizAnswer(currentLesson.id, questionIndex, Number(event.target.value))}><option value="">Selecione</option>{(question.options || []).map((option, optionIndex) => <option key={`quiz-opt-${currentLesson.id}-${optionIndex}`} value={optionIndex}>{option}</option>)}</select> : null}
                                      {question.type === 'text' ? <textarea className="min-h-24 w-full rounded-lg border border-slate-300 p-2 text-sm" value={String((quizAnswers[currentLesson.id] || [])[questionIndex] ?? '')} onChange={(event) => updateQuizAnswer(currentLesson.id, questionIndex, event.target.value)} /> : null}
                                      {question.type === 'attachment' ? <input type="file" className="w-full rounded-lg border border-dashed border-slate-300 bg-white p-2 text-xs file:mr-3 file:rounded file:border-0 file:bg-purple-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-purple-800" onChange={async (event) => { const file = event.target.files?.[0]; if (!file) return; const url = await uploadAnswerAttachment(file); updateQuizAnswer(currentLesson.id, questionIndex, url); }} /> : null}
                                    </div>
                                  ))}
                                  <button type="button" className="mt-3 min-h-11 w-full rounded-xl bg-purple-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => submitQuizForm(selectedCourse.id, currentLesson.id)}>Enviar avaliação</button>
                                </div>
                              ) : null}

                              <div className="sticky bottom-2 z-10 rounded-xl border border-slate-200 bg-white/95 p-2 shadow-sm backdrop-blur sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
                                {selectedCourseProgress?.enrollment?.completedLessonIds?.includes(currentLesson.id) ? (
                                  <button
                                    type="button"
                                    className="min-h-11 w-full rounded-xl bg-slate-700 px-4 py-2 text-sm font-semibold text-white"
                                    onClick={() => markLessonAsNotDone(selectedCourse.id, currentLesson.id)}
                                  >
                                    Reassistir aula
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="min-h-11 w-full rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white"
                                    onClick={() => markLessonAsDone(selectedCourse.id, currentLesson.id)}
                                  >
                                    Concluir aula e liberar próxima
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}

                    <div className="rounded-2xl border border-slate-200 p-3 sm:p-4">
                      <p className="mb-2 text-xs font-semibold text-slate-600">Aulas (resumo)</p>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                        {orderedLessons.map((lesson, index) => {
                          const locked = !unlockedLessonIds.has(lesson.id);
                          const done = selectedCourseProgress?.enrollment?.completedLessonIds?.includes(lesson.id);
                          return (
                            <button
                              key={`lesson-summary-${lesson.id}`}
                              type="button"
                              disabled={locked}
                              onClick={() => setSelectedLessonId(lesson.id)}
                              className={`rounded-xl border p-3 text-left text-xs ${selectedLessonId === lesson.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'} ${locked ? 'cursor-not-allowed opacity-60' : ''}`}
                            >
                              <div className="mb-1 flex items-center justify-between gap-2">
                                <p className="font-semibold text-slate-700">Aula {index + 1}</p>
                                <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{done ? 'Concluída' : locked ? 'Bloqueada' : 'Disponível'}</span>
                              </div>
                              <p className="line-clamp-2 text-slate-700">{lesson.title}</p>
                              <p className="mt-1 text-slate-500">{lesson.durationMinutes || 30} min</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(selectedCourse.modules || [])
                      .flatMap((module) => module.lessons || [])
                      .filter((lesson) =>
                        Boolean(
                          selectedCourseProgress?.enrollment?.completedLessonIds?.includes(
                            lesson.id,
                          ),
                        ),
                      )
                      .map((lesson) => (
                        <div key={lesson.id} className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm">
                          <p className="font-medium text-emerald-800">
                            {lesson.title} ({lesson.durationMinutes || 30} min)
                          </p>
                          <p className="text-emerald-700">{lesson.description}</p>
                        </div>
                      ))}
                    {!selectedCourseProgress?.enrollment?.completedLessonIds?.length ? (
                      <p className="text-xs text-slate-500">Nenhuma aula concluída ainda.</p>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === TABS.LEARNING_PATHS ? (
        <section id={`course-panel-${TABS.LEARNING_PATHS}`} role="tabpanel" aria-labelledby={`course-tab-${TABS.LEARNING_PATHS}`} className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
            <FaProjectDiagram className="text-blue-600" /> Trilhas de Ensino
            <FaInfoCircle className="text-slate-400" title="Monte fluxos de crescimento conectando vários cursos." />
          </h2>
          <p className="mb-4 text-sm text-slate-600">
            Escolha uma trilha para visualizar o fluxo recomendado de cursos. Apenas editores de curso podem cadastrar, editar ou excluir trilhas.
          </p>

          {canManageLearningPaths ? (
            <div className="mb-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
              <h3 className="mb-3 text-sm font-semibold text-blue-800">
                {editingLearningPathId ? 'Editar trilha' : 'Cadastrar nova trilha'}
              </h3>
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                <input
                  type="text"
                  placeholder="Nome da trilha"
                  value={learningPathForm.name}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setLearningPathForm((prev) => ({ ...prev, name: event.target.value }))}
                />
                <input
                  type="text"
                  placeholder="Público-alvo (opcional)"
                  value={learningPathForm.audience}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                  onChange={(event) => setLearningPathForm((prev) => ({ ...prev, audience: event.target.value }))}
                />
              </div>
              <textarea
                placeholder="Descrição da trilha (opcional)"
                value={learningPathForm.description}
                className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                onChange={(event) => setLearningPathForm((prev) => ({ ...prev, description: event.target.value }))}
              />
              <div className="mt-3 grid max-h-64 grid-cols-1 gap-2 overflow-y-auto rounded-xl border border-blue-100 bg-white/70 p-2 md:grid-cols-2">
                {courses.map((course) => {
                  const checked = learningPathForm.courseIds.includes(course.id);
                  return (
                    <label key={`path-course-${course.id}`} className={`cursor-pointer rounded-lg border px-3 py-2 text-sm ${checked ? 'border-blue-400 bg-blue-50 text-blue-800' : 'border-slate-200 bg-white text-slate-700'}`}>
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={checked}
                        onChange={() =>
                          setLearningPathForm((prev) => ({
                            ...prev,
                            courseIds: checked
                              ? prev.courseIds.filter((id) => id !== course.id)
                              : [...prev.courseIds, course.id],
                          }))
                        }
                      />
                      {course.title}
                    </label>
                  );
                })}
              </div>
              <button
                type="button"
                className="mt-3 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white"
                onClick={saveLearningPath}
              >
                <FaPlus className="mr-2 inline" />
                {editingLearningPathId ? 'Salvar alterações' : 'Publicar trilha'}
              </button>
              {editingLearningPathId ? (
                <button
                  type="button"
                  className="ml-2 mt-3 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700"
                  onClick={cancelLearningPathEdit}
                >
                  Cancelar edição
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {learningPaths.map((path) => (
              <div
                key={path.id}
                className={`rounded-2xl border p-4 transition ${selectedLearningPath?.id === path.id ? 'border-blue-600 bg-gradient-to-br from-blue-50 to-white shadow' : 'border-slate-200 bg-white hover:border-blue-200'}`}
              >
                <button type="button" onClick={() => setSelectedLearningPathId(path.id)} className="w-full text-left">
                  <p className="text-base font-semibold text-slate-800">{path.name}</p>
                  <p className="text-xs text-slate-500">{path.description || 'Sem descrição cadastrada.'}</p>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                    {path.courseIds?.length || 0} curso(s) no fluxo
                  </p>
                </button>
                {canManageLearningPaths ? (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700"
                      onClick={() => startEditLearningPath(path)}
                    >
                      <FaEdit />
                      Editar
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700"
                      onClick={() => deleteLearningPath(path.id)}
                    >
                      <FaTrash />
                      Excluir
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          {selectedLearningPath ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
              <h3 className="text-lg font-semibold text-slate-800">{selectedLearningPath.name}</h3>
              <p className="text-sm text-slate-600">
                {selectedLearningPath.description || 'Fluxo de aprendizagem definido para evolução contínua.'}
              </p>
              {selectedLearningPath.audience ? (
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Público recomendado: {selectedLearningPath.audience}
                </p>
              ) : null}
              <div className="mt-4 flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                {(selectedLearningPath.courseIds || []).map((courseId, index) => {
                  const course = courses.find((entry) => entry.id === courseId);
                  return (
                    <React.Fragment key={`flow-${selectedLearningPath.id}-${courseId}`}>
                      <div className="rounded-xl border border-blue-200 bg-white px-3 py-2 shadow-sm">
                        <p className="text-xs font-semibold text-blue-900">{index + 1}º passo</p>
                        <p className="text-sm font-medium text-slate-800">{course?.title || 'Curso indisponível'}</p>
                      </div>
                      {index < selectedLearningPath.courseIds.length - 1 ? (
                        <FaArrowRight className="rotate-90 self-center text-blue-400 sm:rotate-0" />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">Nenhuma trilha disponível no momento.</p>
          )}
        </section>
      ) : null}

      {activeTab === TABS.TEACHER && canReviewActivities ? (
        <section id={`course-panel-${TABS.TEACHER}`} role="tabpanel" aria-labelledby={`course-tab-${TABS.TEACHER}`} className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
            <FaListOl className="text-blue-600" /> Atividades recebidas
            <FaInfoCircle className="text-slate-400" title="Filtre por aluno, curso e turma para avaliação." />
          </h2>
          <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-3">
            <select
              className="rounded border p-2 text-sm"
              value={instructorFilters.studentId}
              onChange={(event) => setInstructorFilters((prev) => ({ ...prev, studentId: event.target.value }))}
            >
              <option value="all">Todos os alunos</option>
              {Array.from(new Map(instructorSubmissions.map((entry) => [entry.studentId, entry.studentName])).entries()).map(([id, name]) => (
                <option key={`filter-student-${id}`} value={id}>{name}</option>
              ))}
            </select>
            <select
              className="rounded border p-2 text-sm"
              value={instructorFilters.courseId}
              onChange={(event) => setInstructorFilters((prev) => ({ ...prev, courseId: event.target.value }))}
            >
              <option value="all">Todos os cursos</option>
              {Array.from(new Map(instructorSubmissions.map((entry) => [entry.courseId, entry.courseTitle])).entries()).map(([id, title]) => (
                <option key={`filter-course-${id}`} value={id}>{title}</option>
              ))}
            </select>
            <select
              className="rounded border p-2 text-sm"
              value={instructorFilters.classroom}
              onChange={(event) => setInstructorFilters((prev) => ({ ...prev, classroom: event.target.value }))}
            >
              <option value="all">Todas as turmas</option>
              {Array.from(new Set(instructorSubmissions.flatMap((entry) => entry.classroomNames || []))).map((name) => (
                <option key={`filter-class-${name}`} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            {filteredInstructorSubmissions.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma atividade recebida ainda.</p>
            ) : null}
            {filteredInstructorSubmissions.map((entry) => (
              <div key={`${entry.type}-${entry.courseId}-${entry.lessonId}-${entry.studentId}-${entry.submittedAt}`} className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-800">{entry.courseTitle}</p>
                <p className="text-xs text-slate-600">
                  Aluno: {entry.studentName} • Aula: {entry.lessonTitle} • Tipo: {entry.type === 'task' ? 'Tarefa' : 'Avaliação'}
                </p>
                <p className="text-xs text-slate-500">Turma(s): {(entry.classroomNames || []).join(', ') || 'Sem turma vinculada'}</p>
                <p className="text-xs text-slate-500">Enviado em: {new Date(entry.submittedAt).toLocaleString('pt-BR')}</p>
                <p className="text-xs text-slate-600">Nota: {entry.score ?? 'N/A'}</p>
                {entry.attachmentUrl ? (
                  <button type="button" onClick={() => downloadTaskAttachment(entry)} className="text-xs text-blue-600 hover:underline">
                    Baixar anexo enviado
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {activeTab === TABS.CLASS_DIARY && canAccessClassDiary ? (
        <section id={`course-panel-${TABS.CLASS_DIARY}`} role="tabpanel" aria-labelledby={`course-tab-${TABS.CLASS_DIARY}`} className="overflow-hidden rounded-3xl border border-slate-300 bg-slate-200 shadow-sm">
          <div className="bg-[#02043b] px-6 py-4 text-white">
            <h2 className="flex items-center justify-center gap-2 text-2xl font-bold">
              <FaClipboardCheck className="text-amber-300" /> Diário de Classe
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-3 p-2 sm:p-4 lg:grid-cols-2">
            <div className="rounded-lg border border-slate-300 bg-white p-3">
              <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-3">
                <select
                  className="rounded border border-slate-400 p-2 text-sm"
                  value={attendanceClassroomId || ''}
                  onChange={(event) => setAttendanceClassroomId(Number(event.target.value) || null)}
                >
                  <option value="">Turma</option>
                  {attendanceClassrooms.map((classroom) => (
                    <option key={`attendance-class-${classroom.id}`} value={classroom.id}>
                      {classroom.name}
                    </option>
                  ))}
                </select>
                <input
                  className="rounded border border-slate-400 p-2 text-sm"
                  type="date"
                  value={attendanceDate}
                  onChange={(event) => setAttendanceDate(event.target.value)}
                />
                <button
                  type="button"
                  className="rounded border border-slate-400 bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60"
                  onClick={saveClassDiary}
                  disabled={!canEditAttendance}
                >
                  {editingDiaryId ? 'Atualizar Diário' : 'Criar Diário'}
                </button>
              </div>
              <div className="mb-3 space-y-2">
                <input
                  className="w-full rounded border border-slate-300 p-2 text-sm"
                  placeholder="Tema da aula"
                  value={diaryTheme}
                  onChange={(event) => setDiaryTheme(event.target.value)}
                  disabled={!canEditAttendance}
                />
                <input
                  className="w-full rounded border border-slate-300 p-2 text-sm"
                  placeholder="Objetivo"
                  value={diaryObjective}
                  onChange={(event) => setDiaryObjective(event.target.value)}
                  disabled={!canEditAttendance}
                />
              </div>
              <div className="mb-2 flex gap-2">
                <button
                  type="button"
                  className="rounded bg-sky-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                  disabled={!selectedDiaryId}
                  onClick={() => setIsRegisteringFrequency(true)}
                >
                  Registrar Frequência
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-400 px-3 py-1 text-xs"
                  onClick={() => {
                    setEditingDiaryId(null);
                    setDiaryTheme('');
                    setDiaryObjective('');
                  }}
                >
                  Novo
                </button>
              </div>

              <div className="max-h-72 overflow-auto rounded border border-slate-400">
                <table className="min-w-[520px] w-full text-xs">
                  <thead className="bg-slate-200 text-slate-700">
                    <tr>
                      <th className="border border-slate-300 px-2 py-1 text-left">ID</th>
                      <th className="border border-slate-300 px-2 py-1 text-left">Nome</th>
                      <th className="border border-slate-300 px-2 py-1 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceRows.map((row) => {
                      const student = studentUsers.find((entry) => Number(entry.id) === Number(row.studentId));
                      const isSelected = Number(selectedAttendanceStudentId) === Number(row.studentId);
                      return (
                        <tr
                          key={`attendance-student-${row.studentId}`}
                          className={isSelected ? 'bg-blue-50' : 'bg-white'}
                          onClick={() => setSelectedAttendanceStudentId(row.studentId)}
                        >
                          <td className="cursor-pointer border border-slate-300 px-2 py-1">{row.studentId}</td>
                          <td className="cursor-pointer border border-slate-300 px-2 py-1">
                            {student?.name || `Aluno #${row.studentId}`}
                          </td>
                          <td className="border border-slate-300 px-2 py-1">{formatAttendanceStatus(row.status)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {attendanceRows.length === 0 ? (
                  <p className="p-3 text-sm text-slate-500">Nenhum aluno para a turma selecionada.</p>
                ) : null}
              </div>
            </div>

            <div className={`space-y-3 ${isRegisteringFrequency ? '' : 'opacity-50'}`}>
              <div className="rounded-lg bg-blue-400 p-4">
                <h3 className="mb-3 text-center text-xl font-semibold text-[#02043b] sm:text-2xl">Registre a Frequência</h3>
                <div className="space-y-2 rounded border border-blue-300 bg-white px-3 py-2 text-sm">
                  <p><span className="font-semibold text-slate-700">Nome:</span> {selectedAttendanceStudent?.name || 'Selecione um aluno na lista'}</p>
                  <p><span className="font-semibold text-slate-700">ID Aluno:</span> {selectedAttendanceStudentId || '-'}</p>
                  <p><span className="font-semibold text-slate-700">Data:</span> {formatCourseDate(attendanceDate)}</p>
                  <p><span className="font-semibold text-slate-700">Tutor:</span> {user?.name || '-'}</p>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 rounded border border-blue-300 bg-white p-2 text-xs text-slate-700 md:grid-cols-5">
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={selectedAttendanceRow?.allDay !== false}
                      disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                      onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'allDay', event.target.checked)}
                    />
                    Dia todo
                  </label>
                  <span className="self-center text-center">Atrasou por</span>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    className="rounded border border-slate-300 px-2 py-1"
                    value={Number(selectedAttendanceRow?.delayHours || 0)}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance || selectedAttendanceRow?.allDay !== false}
                    onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'delayHours', Number(event.target.value || 0))}
                  />
                  <input
                    type="number"
                    min="0"
                    max="59"
                    className="rounded border border-slate-300 px-2 py-1"
                    value={Number(selectedAttendanceRow?.delayMinutes || 0)}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance || selectedAttendanceRow?.allDay !== false}
                    onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'delayMinutes', Number(event.target.value || 0))}
                  />
                  <label className="flex items-center justify-end gap-1">
                    Obs Psic?
                    <input
                      type="checkbox"
                      checked={Boolean(selectedAttendanceRow?.psychologistObservation)}
                      disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                      onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'psychologistObservation', event.target.checked)}
                    />
                  </label>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 rounded border border-blue-300 bg-white p-2 text-xs text-slate-700 md:grid-cols-[1fr_auto]">
                  <input
                    className="rounded border border-slate-300 px-2 py-1"
                    placeholder="Observações do aluno"
                    value={selectedAttendanceRow?.studentObservation || ''}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                    onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'studentObservation', event.target.value)}
                  />
                  <label className="flex items-center gap-1">
                    PCD?
                    <input
                      type="checkbox"
                      checked={Boolean(selectedAttendanceRow?.pcd)}
                      disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                      onChange={(event) => updateAttendanceFieldForStudent(selectedAttendanceStudentId, 'pcd', event.target.checked)}
                    />
                  </label>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
                  <button
                    type="button"
                    className="rounded bg-green-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
                    onClick={() => setAttendanceStatusForStudent(selectedAttendanceStudentId, 'PRESENTE')}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                  >
                    Presente
                  </button>
                  <button
                    type="button"
                    className="rounded bg-red-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
                    onClick={() => setAttendanceStatusForStudent(selectedAttendanceStudentId, 'FALTA')}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                  >
                    Ausente
                  </button>
                  <button
                    type="button"
                    className="rounded bg-blue-700 px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
                    onClick={() => setAttendanceStatusForStudent(selectedAttendanceStudentId, 'JUSTIFICADO')}
                    disabled={!isRegisteringFrequency || !selectedAttendanceStudentId || !canEditAttendance}
                  >
                    Justificado
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    className="rounded bg-indigo-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
                    onClick={saveAttendance}
                    disabled={!isRegisteringFrequency || !canEditAttendance || attendanceSaving}
                  >
                    {attendanceSaving ? 'Salvando...' : 'Salvar Frequência'}
                  </button>
                </div>
              </div>

              <div className="rounded-lg bg-blue-200 p-3">
                <h4 className="mb-2 text-lg font-semibold text-slate-800">Histórico de Frequência</h4>
                <div className="max-h-44 overflow-auto rounded border border-slate-300 bg-white">
                  <table className="min-w-[520px] w-full text-xs">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="border border-slate-300 px-2 py-1 text-left">Data</th>
                        <th className="border border-slate-300 px-2 py-1 text-left">Aluno</th>
                        <th className="border border-slate-300 px-2 py-1 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAttendanceRecords.slice(0, 30).map((entry) => (
                        <tr key={`attendance-history-${entry.id}`}>
                          <td className="border border-slate-300 px-2 py-1">{formatCourseDate(entry.attendanceDate)}</td>
                          <td className="border border-slate-300 px-2 py-1">{studentUsers.find((student) => student.id === entry.studentId)?.name || `Aluno #${entry.studentId}`}</td>
                          <td className="border border-slate-300 px-2 py-1">{formatAttendanceStatus(entry.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-2 mb-3 rounded-xl bg-[#02043b] p-3 text-white sm:mx-4 sm:mb-4 sm:p-4">
            <h3 className="text-center text-xl font-semibold sm:text-2xl">Consulta de frequência</h3>
            <p className="mb-3 text-center text-xs text-slate-200">(Campos abaixo servem apenas para filtrar dados)</p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
              <select className="rounded border border-slate-300 bg-white p-2 text-xs text-slate-700" value={attendanceFilters.classroomId} onChange={(event) => setAttendanceFilters((prev) => ({ ...prev, classroomId: event.target.value }))}>
                <option value="all">Turma</option>
                {attendanceClassrooms.map((classroom) => <option key={`attendance-filter-class-${classroom.id}`} value={classroom.id}>{classroom.name}</option>)}
              </select>
              <select className="rounded border border-slate-300 bg-white p-2 text-xs text-slate-700" value={attendanceFilters.studentId} onChange={(event) => setAttendanceFilters((prev) => ({ ...prev, studentId: event.target.value }))}>
                <option value="all">Nome do aluno</option>
                {studentUsers.map((student) => <option key={`attendance-filter-student-${student.id}`} value={student.id}>{student.name}</option>)}
              </select>
              <select className="rounded border border-slate-300 bg-white p-2 text-xs text-slate-700" value={attendanceFilters.teacherId} onChange={(event) => setAttendanceFilters((prev) => ({ ...prev, teacherId: event.target.value }))}>
                <option value="all">Professor</option>
                {users.map((teacher) => <option key={`attendance-filter-teacher-${teacher.id}`} value={teacher.id}>{teacher.name}</option>)}
              </select>
              <button
                type="button"
                className="rounded bg-sky-600 px-3 py-2 text-xs font-semibold text-white"
                onClick={() => {
                  setAppliedAttendanceFilters(attendanceFilters);
                  setHasQueriedAttendance(true);
                }}
              >
                Consultar
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                  onClick={() => {
                    const emptyFilters = { classroomId: 'all', studentId: 'all', teacherId: 'all' };
                    setAttendanceFilters(emptyFilters);
                    setAppliedAttendanceFilters(emptyFilters);
                    setHasQueriedAttendance(false);
                  }}
                >
                  Limpar
                </button>
                <button type="button" className="flex-1 rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60" disabled={attendanceExporting} onClick={exportAttendancePdf}>Relatório PDF</button>
                <button type="button" className="flex-1 rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60" disabled={attendanceExporting} onClick={exportAttendanceXls}>Relatório XLS</button>
              </div>
            </div>
            <div className="mt-3 flex flex-col gap-1 text-xs text-slate-200 sm:flex-row sm:items-center sm:justify-between">
              <span>{hasQueriedAttendance ? `${filteredAttendanceRecords.length} registro(s) encontrado(s) na consulta.` : 'Exibindo todos os registros. Use os filtros e clique em Consultar.'}</span>
            </div>
            <div className="mt-2 max-h-56 overflow-auto rounded border border-slate-300 bg-white text-xs text-slate-800">
              <table className="min-w-[760px] w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="border border-slate-300 px-2 py-1 text-left">Nome</th>
                    <th className="border border-slate-300 px-2 py-1 text-left">Data</th>
                    <th className="border border-slate-300 px-2 py-1 text-left">Tutor</th>
                    <th className="border border-slate-300 px-2 py-1 text-left">Turma</th>
                    <th className="border border-slate-300 px-2 py-1 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendanceRecords.slice(0, 100).map((entry) => (
                    <tr key={`attendance-query-${entry.id}`}>
                      <td className="border border-slate-300 px-2 py-1">{studentUsers.find((student) => student.id === entry.studentId)?.name || `Aluno #${entry.studentId}`}</td>
                      <td className="border border-slate-300 px-2 py-1">{formatCourseDate(entry.attendanceDate)}</td>
                      <td className="border border-slate-300 px-2 py-1">{users.find((teacher) => teacher.id === entry.teacherId)?.name || `Professor #${entry.teacherId}`}</td>
                      <td className="border border-slate-300 px-2 py-1">{attendanceClassrooms.find((classroom) => classroom.id === entry.classroomId)?.name || `Turma #${entry.classroomId}`}</td>
                      <td className="border border-slate-300 px-2 py-1">{formatAttendanceStatus(entry.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!canEditAttendance ? (
              <p className="mt-2 text-xs text-amber-300">Você pode consultar, mas somente professor/admin pode registrar frequência.</p>
            ) : null}

            <div className="mt-4 rounded border border-slate-400 bg-slate-100 p-3 text-slate-800">
              <h4 className="mb-2 text-base font-semibold">Diários criados (consulta)</h4>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
                <input
                  type="date"
                  className="rounded border border-slate-300 bg-white p-2 text-xs"
                  value={diaryQueryFilters.startDate}
                  onChange={(event) => setDiaryQueryFilters((prev) => ({ ...prev, startDate: event.target.value }))}
                />
                <input
                  type="date"
                  className="rounded border border-slate-300 bg-white p-2 text-xs"
                  value={diaryQueryFilters.endDate}
                  onChange={(event) => setDiaryQueryFilters((prev) => ({ ...prev, endDate: event.target.value }))}
                />
                <select
                  className="rounded border border-slate-300 bg-white p-2 text-xs"
                  value={diaryQueryFilters.teacherId}
                  onChange={(event) => setDiaryQueryFilters((prev) => ({ ...prev, teacherId: event.target.value }))}
                >
                  <option value="all">Professor</option>
                  {users.map((teacher) => <option key={`diary-filter-teacher-${teacher.id}`} value={teacher.id}>{teacher.name}</option>)}
                </select>
                <input
                  className="rounded border border-slate-300 bg-white p-2 text-xs"
                  placeholder="Tema"
                  value={diaryQueryFilters.theme}
                  onChange={(event) => setDiaryQueryFilters((prev) => ({ ...prev, theme: event.target.value }))}
                />
                <button type="button" className="rounded bg-sky-600 px-3 py-2 text-xs font-semibold text-white" onClick={() => setHasQueriedDiaries(true)}>
                  Consultar diários
                </button>
              </div>
              {hasQueriedDiaries ? (
                <div className="mt-3 max-h-44 overflow-auto rounded border border-slate-300 bg-white">
                  <table className="min-w-[520px] w-full text-xs">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="border border-slate-300 px-2 py-1 text-left">Data</th>
                        <th className="border border-slate-300 px-2 py-1 text-left">Tutor</th>
                        <th className="border border-slate-300 px-2 py-1 text-left">Tema</th>
                        <th className="border border-slate-300 px-2 py-1 text-left">Pres/Falt/Jus</th>
                        <th className="border border-slate-300 px-2 py-1 text-left">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDiaryResults.map((diary) => (
                        <tr key={`diary-query-${diary.id}`} className={Number(selectedDiaryId) === Number(diary.id) ? 'bg-blue-50' : ''}>
                          <td className="border border-slate-300 px-2 py-1">{formatCourseDate(diary.diaryDate)}</td>
                          <td className="border border-slate-300 px-2 py-1">{diary.tutorName}</td>
                          <td className="border border-slate-300 px-2 py-1">{diary.theme || '-'}</td>
                          <td className="border border-slate-300 px-2 py-1">{diary.presentCount}/{diary.absentCount}/{diary.justifiedCount}</td>
                          <td className="border border-slate-300 px-2 py-1">
                            <div className="flex flex-wrap gap-1">
                              <button type="button" className="rounded border px-2 py-1" onClick={() => startEditDiary(diary)}>Editar</button>
                              <button type="button" className="rounded border px-2 py-1 text-rose-700" onClick={() => removeDiary(diary.id)}>Excluir</button>
                              <button type="button" className="rounded border px-2 py-1 text-sky-700" onClick={() => { setSelectedDiaryId(diary.id); setIsRegisteringFrequency(true); }}>Freq.</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-600">Use os filtros e clique em "Consultar diários" para exibir os diários criados.</p>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === TABS.CERTIFICATE ? (
        <section id={`course-panel-${TABS.CERTIFICATE}`} role="tabpanel" aria-labelledby={`course-tab-${TABS.CERTIFICATE}`} className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
            Consulta de certificados
            <FaInfoCircle className="cursor-help text-slate-400" title="Validação institucional por ID de referência. A consulta pública também está disponível em /certificados/consulta." />
          </h2>
          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-0 w-full flex-1 rounded-xl border border-slate-300 p-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="Informe o ID de referência (ex.: LMB-2026-000001)"
              value={certificateQuery}
              onChange={(event) => setCertificateQuery(event.target.value)}
            />
            <button
              type="button"
              className="min-h-11 w-full rounded-xl bg-[#0b3474] px-4 py-2 text-sm font-semibold text-white sm:w-auto"
              onClick={consultCertificate}
            >
              Consultar
            </button>
          </div>
          {certificateResult ? (
            <div className="mt-3 rounded border border-slate-200 p-3 text-sm">
              <p><strong>Referência:</strong> {certificateResult.referenceId}</p>
              <p><strong>Aluno:</strong> {certificateResult.userName}</p>
              <p><strong>Curso:</strong> {certificateResult.courseTitle}</p>
              <p><strong>Carga horária:</strong> {certificateResult.estimatedHours}h</p>
              <p><strong>Emitido em:</strong> {new Date(certificateResult.issuedAt).toLocaleString('pt-BR')}</p>
            </div>
          ) : null}
        </section>
      ) : null}

      {activeTab === TABS.CREATOR && canViewEditor ? (
        <section id={`course-panel-${TABS.CREATOR}`} role="tabpanel" aria-labelledby={`course-tab-${TABS.CREATOR}`} className="overflow-hidden rounded-3xl border border-blue-100 bg-slate-50/70 shadow-sm">
          <div className="bg-gradient-to-r from-[#071f54] via-[#0b3474] to-[#0d4d91] p-5 text-white sm:p-6">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-xl"><FaLayerGroup /></span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-200">Editor de cursos</p>
                    <h2 className="mt-0.5 truncate text-xl font-bold sm:text-2xl">{editingCourseId ? (form.title || 'Editar curso') : 'Criar novo curso'}</h2>
                    <p className="mt-1 text-xs text-blue-100">Monte o curso por etapas: informações, conteúdo e certificado.</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-semibold">
                  <span className="rounded-full bg-white/10 px-3 py-1.5">{form.modules.length} módulo(s)</span>
                  <span className="rounded-full bg-white/10 px-3 py-1.5">{form.modules.reduce((total, module) => total + (module.lessons?.length || 0), 0)} aula(s)</span>
                  <span className="rounded-full bg-white/10 px-3 py-1.5">{getFormDurationMinutes(form)} min</span>
                  <span className="rounded-full bg-white/10 px-3 py-1.5">{form.requiresAccessGrant ? 'Acesso controlado' : 'Acesso livre'}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold text-white transition hover:bg-white/20"
                  onClick={resetCreatorForm}
                >
                  <FaFolderPlus /> Novo curso
                </button>
                {canCreate ? (
                  <button
                    type="button"
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 text-xs font-bold text-[#071f54] shadow-sm transition hover:bg-blue-50 disabled:opacity-60"
                    onClick={saveCourse}
                    disabled={saving}
                  >
                    <FaSave /> {saving ? 'Salvando...' : editingCourseId ? 'Salvar alterações' : 'Salvar curso'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="border-b border-slate-200 bg-white px-4 py-3 sm:px-5">
            <div className="grid gap-2 sm:grid-cols-3">
              {[
                ['overview', '1. Informações', 'Dados principais, acesso e pré-requisitos'],
                ['content', '2. Conteúdo', 'Módulos, aulas, materiais e atividades'],
                ['certificate', '3. Certificado', 'Identidade e assinatura do certificado'],
              ].map(([id, label, helper]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setCreatorPanel(id)}
                  className={`rounded-2xl border px-4 py-3 text-left transition ${creatorPanel === id ? 'border-blue-300 bg-blue-50 shadow-sm ring-2 ring-blue-50' : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-slate-50'}`}
                >
                  <span className={`block text-xs font-bold ${creatorPanel === id ? 'text-blue-700' : 'text-slate-700'}`}>{label}</span>
                  <span className="mt-1 block text-[11px] leading-relaxed text-slate-500">{helper}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-3 sm:p-5">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/50 p-4 xl:col-span-2">
              {creatorPanel === 'overview' ? (
                <>
              <section className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><FaBookOpen /></span>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">Dados principais</h3>
                    <p className="text-xs text-slate-500">Informações que identificam o curso para alunos e equipe.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                    Título do curso
                    <input className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" placeholder="Ex.: Introdução ao Atendimento" value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} />
                  </label>
                  <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                    Carga horária calculada
                    <input
                      className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-normal text-slate-600 outline-none"
                      type="text"
                      readOnly
                      value={`${getFormDurationMinutes(form)} minutos`}
                      title="Soma automática da duração de todas as aulas"
                    />
                  </label>
                  <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 md:col-span-2">
                    Descrição
                    <textarea className="min-h-[96px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" placeholder="Explique o objetivo e o conteúdo do curso" value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} />
                  </label>
                  <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 md:col-span-2">
                    E-mail para receber atividades
                    <input
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                      type="email"
                      placeholder="professor@instituicao.org.br"
                      value={form.activityNotificationEmail || ''}
                      onChange={(event) => setForm((prev) => ({ ...prev, activityNotificationEmail: event.target.value }))}
                    />
                  </label>
                </div>
              </section>
              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3">
                  <p className="text-sm font-semibold text-slate-800">Capa do curso</p>
                  <p className="mt-0.5 text-xs text-slate-500">Use uma imagem horizontal para deixar o catálogo mais uniforme.</p>
                </div>
                <div className="overflow-hidden rounded-2xl border border-dashed border-slate-200 bg-slate-50">
                  {localAssetPreviewUrls.cover ? (
                    <img src={localAssetPreviewUrls.cover} alt="Prévia da capa do curso" className="h-44 w-full object-cover" />
                  ) : form.coverImageUrl ? (
                    <CourseCover course={{ id: editingCourseId, coverImageUrl: form.coverImageUrl, title: form.title }} alt="Capa do curso" className="h-44 w-full object-cover" />
                  ) : (
                    <div className="flex h-36 flex-col items-center justify-center px-4 text-center">
                      <FaBookOpen className="text-2xl text-slate-300" />
                      <p className="mt-2 text-xs font-semibold text-slate-500">Nenhuma capa selecionada</p>
                      <p className="mt-1 text-[11px] text-slate-400">PNG ou JPG em formato horizontal.</p>
                    </div>
                  )}
                </div>
                <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-100">
                  <FaPlus /> {form.coverImageUrl || localAssetPreviewUrls.cover ? 'Trocar imagem' : 'Selecionar imagem'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      await uploadCourseCover(file);
                    }}
                  />
                </label>
              </section>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                  Nível de dificuldade
                  <select className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" value={form.difficulty} onChange={(event) => setForm((prev) => ({ ...prev, difficulty: event.target.value }))}>
                    <option value="iniciante">Iniciante</option>
                    <option value="intermediario">Intermediário</option>
                    <option value="avancado">Avançado</option>
                  </select>
                </label>
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50/40">
                  <input type="checkbox" checked={form.requiresAccessGrant} onChange={(event) => setForm((prev) => ({ ...prev, requiresAccessGrant: event.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                  <span><strong className="block">Acesso controlado</strong><span className="font-normal text-slate-500">Exigir turma ou liberação individual.</span></span>
                </label>
              </div>
              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="mb-1 text-sm font-semibold text-slate-700">Pré-requisitos</p>
                <p className="mb-3 text-xs text-slate-500">Selecione cursos que precisam ser concluídos antes deste.</p>
                <div className="max-h-36 space-y-1 overflow-auto rounded-xl border border-slate-200 bg-slate-50 p-2 text-xs">
                  {courses
                    .filter((course) => course.id !== editingCourseId)
                    .map((course) => (
                      <label key={`pre-${course.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-slate-700 transition hover:bg-white">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-blue-600"
                          checked={(form.prerequisiteCourseIds || []).includes(course.id)}
                          onChange={() =>
                            setForm((prev) => ({
                              ...prev,
                              prerequisiteCourseIds: toggleMultiSelect(prev.prerequisiteCourseIds || [], course.id),
                            }))
                          }
                        />
                        {course.title}
                      </label>
                    ))}
                </div>
              </section>
                </>
              ) : null}

              {creatorPanel === 'certificate' ? (
                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-blue-50/60 p-4">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><FaClipboardCheck /></span>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-800">Configuração do certificado</h3>
                        <p className="mt-0.5 text-xs text-slate-500">Defina os dados institucionais, assinatura e aparência do documento.</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-5 p-4 sm:p-5">
                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                        Razão social
                        <input
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                          placeholder="Nome da instituição"
                          value={form.certificateConfig?.companyName || ''}
                          onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), companyName: event.target.value } }))}
                        />
                      </label>
                      <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                        CNPJ
                        <input
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                          placeholder="00.000.000/0000-00"
                          value={form.certificateConfig?.cnpj || ''}
                          onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), cnpj: event.target.value } }))}
                        />
                      </label>
                      <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                        Endereço
                        <input
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                          placeholder="Endereço institucional"
                          value={form.certificateConfig?.address || ''}
                          onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), address: event.target.value } }))}
                        />
                      </label>
                      <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                        Contato
                        <input
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                          placeholder="Telefone, site ou e-mail"
                          value={form.certificateConfig?.contact || ''}
                          onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), contact: event.target.value } }))}
                        />
                      </label>
                      <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 md:col-span-2">
                        Responsável pela assinatura
                        <input
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                          placeholder="Nome de quem assina o certificado"
                          value={form.certificateConfig?.signerName || ''}
                          onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), signerName: event.target.value } }))}
                        />
                      </label>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                        <p className="text-xs font-semibold text-slate-600">Cor principal</p>
                        <div className="mt-2 flex items-center gap-3">
                          <input
                            className="h-10 w-14 cursor-pointer rounded-xl border border-slate-200 bg-white p-1"
                            type="color"
                            value={form.certificateConfig?.primaryColor || '#0f766e'}
                            onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), primaryColor: event.target.value } }))}
                            title="Cor principal do certificado"
                          />
                          <div>
                            <span className="block h-6 w-20 rounded-lg border border-white shadow-sm" style={{ backgroundColor: form.certificateConfig?.primaryColor || '#0f766e' }} />
                            <span className="mt-1 block text-[10px] text-slate-400">{form.certificateConfig?.primaryColor || '#0f766e'}</span>
                          </div>
                        </div>
                      </div>
                      <label className="flex flex-col gap-1.5 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs font-semibold text-slate-600">
                        Posição da logo
                        <select
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                          value={form.certificateConfig?.logoPosition || 'center'}
                          onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), logoPosition: event.target.value } }))}
                        >
                          <option value="left">À esquerda</option>
                          <option value="center">Centralizada</option>
                          <option value="right">À direita</option>
                        </select>
                      </label>
                    </div>

                    <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                      Texto do certificado
                      <textarea
                        className="min-h-[110px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal leading-relaxed text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                        placeholder="Texto exibido no certificado"
                        value={form.certificateConfig?.bodyTextTemplate || ''}
                        onChange={(event) => setForm((prev) => ({ ...prev, certificateConfig: { ...(prev.certificateConfig || {}), bodyTextTemplate: event.target.value } }))}
                      />
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {['{{ALUNO}}', '{{CURSO}}', '{{CARGA_HORARIA}}'].map((variable) => (
                        <span key={variable} className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500">{variable}</span>
                      ))}
                    </div>

                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-16 w-24 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
                            {localAssetPreviewUrls.certificateLogo ? (
                              <img src={localAssetPreviewUrls.certificateLogo} alt="Prévia da logo do certificado" className="h-full w-full object-contain p-1" />
                            ) : form.certificateConfig?.logoUrl ? (
                              <CertificateLogoPreview courseId={editingCourseId} logoUrl={form.certificateConfig.logoUrl} className="h-full w-full object-contain p-1" />
                            ) : (
                              <span className="text-[10px] font-semibold text-slate-300">SEM LOGO</span>
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-700">Logo do certificado</p>
                            <p className="mt-1 text-[11px] text-slate-400">Preferencialmente PNG com fundo transparente.</p>
                          </div>
                        </div>
                        <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-100">
                          <FaPlus /> Selecionar logo
                          <input
                            className="hidden"
                            type="file"
                            accept="image/*"
                            onChange={async (event) => {
                              const file = event.target.files?.[0];
                              if (!file) return;
                              await uploadCertificateLogo(file);
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {creatorPanel === 'content' ? (
                <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-100 bg-blue-50/45 p-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Estrutura do curso</h3>
                  <p className="mt-0.5 text-xs text-slate-500">Organize módulos e aulas. Abra somente o módulo em que estiver trabalhando.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-blue-700 shadow-sm">{form.modules.length} módulo(s)</span>
                  <button
                    type="button"
                    onClick={() => setExpandedCreatorModules(Object.fromEntries(form.modules.map((_, index) => [index, true])))}
                    className="rounded-lg border border-blue-200 bg-white px-2.5 py-1.5 text-[10px] font-bold text-blue-700"
                  >
                    Expandir todos
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpandedCreatorModules({})}
                    className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-bold text-slate-500"
                  >
                    Recolher
                  </button>
                </div>
              </div>

              {form.modules.map((module, moduleIndex) => (
                <div key={`module-${moduleIndex}`} className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition ${expandedCreatorModules[moduleIndex] ? 'border-blue-200 ring-2 ring-blue-50' : 'border-slate-200'}`}>
                  <div className="flex flex-wrap items-center gap-2 bg-gradient-to-r from-slate-50 to-white p-3 sm:p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#0b3474] text-xs font-bold text-white">{moduleIndex + 1}</span>
                    <input className="min-w-[220px] flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" placeholder={`Nome do módulo ${moduleIndex + 1}`} value={module.title} onChange={(event) => updateModule(moduleIndex, { ...module, title: event.target.value })} />
                    <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-500 shadow-sm">{module.lessons?.length || 0} aula(s)</span>
                    <button
                      type="button"
                      onClick={() => setExpandedCreatorModules((current) => ({ ...current, [moduleIndex]: !current[moduleIndex] }))}
                      className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-blue-50 hover:text-blue-700"
                      aria-label={expandedCreatorModules[moduleIndex] ? 'Recolher módulo' : 'Expandir módulo'}
                    >
                      <span className={`text-lg transition-transform ${expandedCreatorModules[moduleIndex] ? 'rotate-180' : ''}`}>⌄</span>
                    </button>
                    {form.modules.length > 1 ? (
                      <button
                        type="button"
                        className="min-h-10 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
                        onClick={() => setForm((prev) => ({ ...prev, modules: prev.modules.filter((_, index) => index !== moduleIndex) }))}
                      >
                        <FaTrash className="mr-1 inline" /> Remover
                      </button>
                    ) : null}
                  </div>
                  {expandedCreatorModules[moduleIndex] ? (
                    <div className="space-y-3 border-t border-slate-100 p-3 sm:p-4">
                  <textarea
                    className="mb-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                    rows={2}
                    placeholder="Descrição do módulo"
                    value={module.description || ''}
                    onChange={(event) => updateModule(moduleIndex, { ...module, description: event.target.value })}
                  />
                  {module.lessons.map((lesson, lessonIndex) => (
                    <div key={`lesson-${moduleIndex}-${lessonIndex}`} className={`overflow-hidden rounded-2xl border bg-slate-50/70 transition ${expandedCreatorLessons[`${moduleIndex}:${lessonIndex}`] ? 'border-blue-200' : 'border-slate-200'}`}>
                      <div className="flex flex-wrap items-center gap-2 bg-white p-3">
                        <span className="rounded-lg bg-blue-50 px-2.5 py-1.5 text-[10px] font-bold text-blue-700">Aula {lessonIndex + 1}</span>
                        <input
                          className="min-w-[220px] flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                          placeholder={`Nome da aula ${lessonIndex + 1}`}
                          value={lesson.title}
                          onChange={(event) => {
                            const lessons = module.lessons.map((entry, idx) => idx === lessonIndex ? { ...entry, title: event.target.value } : entry);
                            updateModule(moduleIndex, { ...module, lessons });
                          }}
                        />
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500">{lesson.durationMinutes || 30} min</span>
                        {(lesson.materials || []).length > 0 && <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[10px] font-semibold text-sky-700">{lesson.materials.length} material(is)</span>}
                        {(lesson.tasks || []).length > 0 && <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-700">{lesson.tasks.length} tarefa(s)</span>}
                        {lesson.quiz && <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-semibold text-violet-700">Avaliação</span>}
                        <button
                          type="button"
                          onClick={() => setExpandedCreatorLessons((current) => {
                            const key = `${moduleIndex}:${lessonIndex}`;
                            return { ...current, [key]: !current[key] };
                          })}
                          className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-blue-50 hover:text-blue-700"
                          aria-label={expandedCreatorLessons[`${moduleIndex}:${lessonIndex}`] ? 'Recolher aula' : 'Expandir aula'}
                        >
                          <span className={`text-lg transition-transform ${expandedCreatorLessons[`${moduleIndex}:${lessonIndex}`] ? 'rotate-180' : ''}`}>⌄</span>
                        </button>
                      </div>
                      {expandedCreatorLessons[`${moduleIndex}:${lessonIndex}`] ? (
                        <div className="space-y-3 border-t border-slate-100 p-3">
                      <textarea className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" placeholder="Descrição da aula" value={lesson.description} onChange={(event) => {
                        const lessons = module.lessons.map((entry, idx) => idx === lessonIndex ? { ...entry, description: event.target.value } : entry);
                        updateModule(moduleIndex, { ...module, lessons });
                      }} />
                      <label className="mb-1 block text-[11px] font-semibold text-slate-600">Tempo da aula (em minutos)</label>
                      <input
                        className="mb-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                        type="number"
                        min="1"
                        placeholder="Ex.: 30 minutos"
                        value={lesson.durationMinutes || 30}
                        onChange={(event) => {
                          const minutes = Math.max(1, Number(event.target.value || 1));
                          const lessons = module.lessons.map((entry, idx) =>
                            idx === lessonIndex ? { ...entry, durationMinutes: minutes } : entry,
                          );
                          updateModule(moduleIndex, { ...module, lessons });
                        }}
                      />
                      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2 text-xs">
                        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 font-semibold text-sky-700 transition hover:bg-sky-100">
                          <FaPlus /> Material
                          <input
                            className="hidden"
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,image/*"
                            onChange={async (event) => {
                              const file = event.target.files?.[0];
                              if (!file) return;
                              if (isVideoFile(file)) {
                                setMessage('Vídeos não podem ser enviados por arquivo. Hospede o vídeo em outra plataforma e anexe somente o link.');
                                event.target.value = '';
                                return;
                              }
                              await handleUploadMaterial(moduleIndex, lessonIndex, file);
                              event.target.value = '';
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 font-semibold text-rose-700 transition hover:bg-rose-100"
                          onClick={() => handleAddVideoLinkMaterial(moduleIndex, lessonIndex)}
                        >
                          + Link de vídeo
                        </button>
                        <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 font-semibold text-amber-700 transition hover:bg-amber-100" onClick={() => {
                          const lessons = module.lessons.map((entry, idx) => {
                            if (idx !== lessonIndex) return entry;
                            const existingTasks = Array.isArray(entry.tasks) ? entry.tasks : [];
                            return {
                              ...entry,
                              tasks: [
                                ...existingTasks,
                                {
                                  title: `Tarefa ${existingTasks.length + 1}`,
                                  description: '',
                                  instructions: '',
                                  maxScore: 10,
                                  dueDays: 7,
                                  formQuestions: [],
                                },
                              ],
                            };
                          });
                          updateModule(moduleIndex, { ...module, lessons });
                        }}><FaPlus /> Tarefa</button>
                        <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 font-semibold text-violet-700 transition hover:bg-violet-100" onClick={() => {
                          const lessons = module.lessons.map((entry, idx) => idx === lessonIndex ? { ...entry, quiz: { title: 'Avaliação', maxScore: 10, passingScore: 7, questions: [] } } : entry);
                          updateModule(moduleIndex, { ...module, lessons });
                        }}><FaPlus /> Avaliação</button>
                        <button type="button" className="rounded-lg border border-rose-200 bg-white px-3 py-2 font-semibold text-rose-600 transition hover:bg-rose-50" onClick={() => {
                          const lessons = module.lessons.filter((_, idx) => idx !== lessonIndex);
                          updateModule(moduleIndex, { ...module, lessons: lessons.length ? lessons : [emptyLesson()] });
                        }}><FaTrash className="mr-1 inline" /> Remover aula</button>
                      </div>
                      <div className="mt-3 space-y-3">
                        {(lesson.materials || []).map((material, materialIndex) => (
                          <div key={`lesson-material-editor-${moduleIndex}-${lessonIndex}-${materialIndex}`} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-[11px] font-semibold text-slate-700">Anexo {materialIndex + 1}</p>
                              <button
                                type="button"
                                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[10px] font-semibold text-slate-500 transition hover:bg-slate-50"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) => {
                                    if (idx !== lessonIndex) return entry;
                                    return {
                                      ...entry,
                                      materials: (entry.materials || []).filter((_, idxMaterial) => idxMaterial !== materialIndex),
                                    };
                                  });
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                Excluir anexo
                              </button>
                            </div>
                            <input
                              className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                              placeholder="Nome do anexo"
                              value={material.name || ''}
                              onChange={(event) => {
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    materials: (entry.materials || []).map((entryMaterial, idxMaterial) =>
                                      idxMaterial === materialIndex ? { ...entryMaterial, name: event.target.value } : entryMaterial,
                                    ),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <input
                              className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                              placeholder="URL do anexo (arquivo ou YouTube)"
                              value={getMaterialSourceUrl(material)}
                              onChange={(event) => {
                                const nextUrl = event.target.value;
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    materials: (entry.materials || []).map((entryMaterial, idxMaterial) => {
                                      if (idxMaterial !== materialIndex) return entryMaterial;
                                      const youtube = Boolean(getYouTubeVideoId(nextUrl));
                                      if (isYouTubeHost(nextUrl) && !youtube) {
                                        setMessage('URL do YouTube inválida. Use o link direto do vídeo para exibição correta no mobile.');
                                      }
                                      return {
                                        ...entryMaterial,
                                        url: nextUrl,
                                        downloadUrl: nextUrl,
                                        relativePath: null,
                                        downloadPath: null,
                                        type: youtube ? 'video' : entryMaterial.type || 'arquivo',
                                        mimeType: youtube ? 'video/youtube' : entryMaterial.mimeType,
                                        addedAt: entryMaterial.addedAt || new Date().toISOString(),
                                      };
                                    }),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <p className="text-[10px] uppercase tracking-wide text-slate-500">
                              Tipo: {material.type || 'arquivo'} {isYoutubeMaterial(material) ? '• YouTube' : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 space-y-3">
                        {(lesson.tasks || []).map((task, taskIndex) => (
                          <div key={`task-editor-${moduleIndex}-${lessonIndex}-${taskIndex}`} className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-emerald-900">Tarefa {taskIndex + 1}</p>
                              <button
                                type="button"
                                className="rounded-lg border border-emerald-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-50"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) => {
                                    if (idx !== lessonIndex) return entry;
                                    return {
                                      ...entry,
                                      tasks: (entry.tasks || []).filter((_, entryTaskIndex) => entryTaskIndex !== taskIndex),
                                    };
                                  });
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                Remover tarefa
                              </button>
                            </div>
                            <input
                              className="mb-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                              placeholder="Título da tarefa"
                              value={task.title || ''}
                              onChange={(event) => {
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                      entryTaskIndex === taskIndex ? { ...entryTask, title: event.target.value } : entryTask,
                                    ),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <textarea
                              className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                              placeholder="Instruções da tarefa"
                              value={task.instructions || ''}
                              onChange={(event) => {
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                      entryTaskIndex === taskIndex ? { ...entryTask, instructions: event.target.value } : entryTask,
                                    ),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <input
                              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                              type="number"
                              min="0"
                              step="0.5"
                              placeholder="Nota máxima da tarefa"
                              value={Number(task.maxScore ?? 10)}
                              onChange={(event) => {
                                const nextMaxScore = Math.max(0, Number(event.target.value || 0));
                                const lessons = module.lessons.map((entry, idx) => {
                                  if (idx !== lessonIndex) return entry;
                                  return {
                                    ...entry,
                                    tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                      entryTaskIndex === taskIndex ? { ...entryTask, maxScore: nextMaxScore } : entryTask,
                                    ),
                                  };
                                });
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <div className="mt-3 space-y-3">
                              {(task.formQuestions || []).map((question, questionIndex) => (
                                <div key={`task-question-${moduleIndex}-${lessonIndex}-${taskIndex}-${questionIndex}`} className="rounded-xl border border-emerald-200 bg-white p-3">
                                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-[11px] font-semibold text-emerald-900">Pergunta {questionIndex + 1}</p>
                                    <button
                                      type="button"
                                      className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[10px] font-semibold text-slate-500 transition hover:bg-slate-50"
                                      onClick={() => {
                                        const lessons = module.lessons.map((entry, idx) => {
                                          if (idx !== lessonIndex) return entry;
                                          return {
                                            ...entry,
                                            tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                              entryTaskIndex === taskIndex
                                                ? {
                                                    ...entryTask,
                                                    formQuestions: (entryTask.formQuestions || []).filter((_, i) => i !== questionIndex),
                                                  }
                                                : entryTask,
                                            ),
                                          };
                                        });
                                        updateModule(moduleIndex, { ...module, lessons });
                                      }}
                                    >
                                      Remover
                                    </button>
                                  </div>
                                  <input
                                    className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                                    placeholder="Texto da pergunta"
                                    value={question.prompt || ''}
                                    onChange={(event) => {
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex) return entry;
                                        return {
                                          ...entry,
                                          tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                            entryTaskIndex === taskIndex
                                              ? {
                                                  ...entryTask,
                                                  formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                    i === questionIndex ? { ...entryQuestion, prompt: event.target.value } : entryQuestion,
                                                  ),
                                                }
                                              : entryTask,
                                          ),
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  />
                                  <select
                                    className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                                    value={question.type || 'text'}
                                    onChange={(event) => {
                                      const nextType = event.target.value;
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex) return entry;
                                        return {
                                          ...entry,
                                          tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                            entryTaskIndex === taskIndex
                                              ? {
                                                  ...entryTask,
                                                  formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                    i === questionIndex ? { ...entryQuestion, ...emptyQuestion(nextType), prompt: entryQuestion.prompt || '' } : entryQuestion,
                                                  ),
                                                }
                                              : entryTask,
                                          ),
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  >
                                    <option value="text">Texto</option>
                                    <option value="multiple_choice">Múltipla escolha</option>
                                    <option value="attachment">Anexo</option>
                                  </select>
                                  <input
                                    className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    placeholder="Pontuação da pergunta"
                                    value={Number(question.score ?? 1)}
                                    onChange={(event) => {
                                      const nextScore = Math.max(0, Number(event.target.value || 0));
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex) return entry;
                                        return {
                                          ...entry,
                                          tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                            entryTaskIndex === taskIndex
                                              ? {
                                                  ...entryTask,
                                                  formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                    i === questionIndex ? { ...entryQuestion, score: nextScore } : entryQuestion,
                                                  ),
                                                }
                                              : entryTask,
                                          ),
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  />
                                  {question.type === 'multiple_choice' ? (
                                    <div className="space-y-1">
                                      {(question.options || []).map((option, optionIndex) => (
                                        <div key={`task-option-editor-${optionIndex}`} className="flex items-center gap-1">
                                          <input
                                            className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                                            placeholder={`Opção ${optionIndex + 1}`}
                                            value={option}
                                            onChange={(event) => {
                                              const lessons = module.lessons.map((entry, idx) => {
                                                if (idx !== lessonIndex) return entry;
                                                return {
                                                  ...entry,
                                                  tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                                    entryTaskIndex === taskIndex
                                                      ? {
                                                          ...entryTask,
                                                          formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) => {
                                                            if (i !== questionIndex) return entryQuestion;
                                                            const options = [...(entryQuestion.options || [])];
                                                            options[optionIndex] = event.target.value;
                                                            return { ...entryQuestion, options };
                                                          }),
                                                        }
                                                      : entryTask,
                                                  ),
                                                };
                                              });
                                              updateModule(moduleIndex, { ...module, lessons });
                                            }}
                                          />
                                          <button
                                            type="button"
                                            className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[10px] font-semibold text-slate-500 transition hover:bg-slate-50"
                                            onClick={() => {
                                              const lessons = module.lessons.map((entry, idx) => {
                                                if (idx !== lessonIndex) return entry;
                                                return {
                                                  ...entry,
                                                  tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                                    entryTaskIndex === taskIndex
                                                      ? {
                                                          ...entryTask,
                                                          formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                            i === questionIndex
                                                              ? { ...entryQuestion, options: (entryQuestion.options || []).filter((_, oi) => oi !== optionIndex) }
                                                              : entryQuestion,
                                                          ),
                                                        }
                                                      : entryTask,
                                                  ),
                                                };
                                              });
                                              updateModule(moduleIndex, { ...module, lessons });
                                            }}
                                          >
                                            x
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50"
                                        onClick={() => {
                                          const lessons = module.lessons.map((entry, idx) => {
                                            if (idx !== lessonIndex) return entry;
                                            return {
                                              ...entry,
                                              tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                                entryTaskIndex === taskIndex
                                                  ? {
                                                      ...entryTask,
                                                      formQuestions: (entryTask.formQuestions || []).map((entryQuestion, i) =>
                                                        i === questionIndex
                                                          ? { ...entryQuestion, options: [...(entryQuestion.options || []), ''] }
                                                          : entryQuestion,
                                                      ),
                                                    }
                                                  : entryTask,
                                              ),
                                            };
                                          });
                                          updateModule(moduleIndex, { ...module, lessons });
                                        }}
                                      >
                                        + Opção
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                              <button
                                type="button"
                                className="rounded-lg border border-emerald-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-50"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) => {
                                    if (idx !== lessonIndex) return entry;
                                    return {
                                      ...entry,
                                      tasks: (entry.tasks || []).map((entryTask, entryTaskIndex) =>
                                        entryTaskIndex === taskIndex
                                          ? { ...entryTask, formQuestions: [...(entryTask.formQuestions || []), emptyQuestion()] }
                                          : entryTask,
                                      ),
                                    };
                                  });
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                + Pergunta da tarefa
                              </button>
                            </div>
                          </div>
                        ))}

                        {lesson.quiz ? (
                          <div className="rounded-2xl border border-violet-200 bg-violet-50/60 p-3">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-purple-900">Avaliação configurada</p>
                              <button
                                type="button"
                                className="rounded-lg border border-violet-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-violet-700 transition hover:bg-violet-50"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) =>
                                    idx === lessonIndex ? { ...entry, quiz: null } : entry,
                                  );
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                Remover avaliação
                              </button>
                            </div>
                            <input
                              className="mb-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                              placeholder="Título da avaliação"
                              value={lesson.quiz.title || ''}
                              onChange={(event) => {
                                const lessons = module.lessons.map((entry, idx) =>
                                  idx === lessonIndex
                                    ? { ...entry, quiz: { ...(entry.quiz || {}), title: event.target.value } }
                                    : entry,
                                );
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <input
                              className="mb-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                              type="number"
                              min="0"
                              step="0.5"
                              placeholder="Nota máxima da avaliação"
                              value={Number(lesson.quiz.maxScore ?? 10)}
                              onChange={(event) => {
                                const nextMaxScore = Math.max(0, Number(event.target.value || 0));
                                const lessons = module.lessons.map((entry, idx) =>
                                  idx === lessonIndex && entry.quiz
                                    ? { ...entry, quiz: { ...entry.quiz, maxScore: nextMaxScore } }
                                    : entry,
                                );
                                updateModule(moduleIndex, { ...module, lessons });
                              }}
                            />
                            <div className="space-y-3">
                              {(lesson.quiz.questions || []).map((question, questionIndex) => (
                                <div key={`quiz-question-${moduleIndex}-${lessonIndex}-${questionIndex}`} className="rounded-xl border border-violet-200 bg-white p-3">
                                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-[11px] font-semibold text-purple-900">Pergunta {questionIndex + 1}</p>
                                    <button
                                      type="button"
                                      className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[10px] font-semibold text-slate-500 transition hover:bg-slate-50"
                                      onClick={() => {
                                        const lessons = module.lessons.map((entry, idx) => {
                                          if (idx !== lessonIndex || !entry.quiz) return entry;
                                          return {
                                            ...entry,
                                            quiz: {
                                              ...entry.quiz,
                                              questions: (entry.quiz.questions || []).filter((_, i) => i !== questionIndex),
                                            },
                                          };
                                        });
                                        updateModule(moduleIndex, { ...module, lessons });
                                      }}
                                    >
                                      Remover
                                    </button>
                                  </div>
                                  <input
                                    className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                                    placeholder="Texto da pergunta"
                                    value={question.prompt || ''}
                                    onChange={(event) => {
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex || !entry.quiz) return entry;
                                        return {
                                          ...entry,
                                          quiz: {
                                            ...entry.quiz,
                                            questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                              i === questionIndex ? { ...entryQuestion, prompt: event.target.value } : entryQuestion,
                                            ),
                                          },
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  />
                                  <select
                                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                                    value={question.type || 'text'}
                                    onChange={(event) => {
                                      const nextType = event.target.value;
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex || !entry.quiz) return entry;
                                        return {
                                          ...entry,
                                          quiz: {
                                            ...entry.quiz,
                                            questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                              i === questionIndex ? { ...entryQuestion, ...emptyQuestion(nextType), prompt: entryQuestion.prompt || '' } : entryQuestion,
                                            ),
                                          },
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  >
                                    <option value="text">Texto</option>
                                    <option value="multiple_choice">Múltipla escolha</option>
                                    <option value="attachment">Anexo</option>
                                  </select>
                                  <input
                                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    placeholder="Pontuação da pergunta"
                                    value={Number(question.score ?? 1)}
                                    onChange={(event) => {
                                      const nextScore = Math.max(0, Number(event.target.value || 0));
                                      const lessons = module.lessons.map((entry, idx) => {
                                        if (idx !== lessonIndex || !entry.quiz) return entry;
                                        return {
                                          ...entry,
                                          quiz: {
                                            ...entry.quiz,
                                            questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                              i === questionIndex ? { ...entryQuestion, score: nextScore } : entryQuestion,
                                            ),
                                          },
                                        };
                                      });
                                      updateModule(moduleIndex, { ...module, lessons });
                                    }}
                                  />
                                  {question.type === 'multiple_choice' ? (
                                    <div className="mt-1 space-y-1">
                                      {(question.options || []).map((option, optionIndex) => (
                                        <div key={`quiz-option-editor-${optionIndex}`} className="flex items-center gap-1">
                                          <input
                                            className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                                            placeholder={`Opção ${optionIndex + 1}`}
                                            value={option}
                                            onChange={(event) => {
                                              const lessons = module.lessons.map((entry, idx) => {
                                                if (idx !== lessonIndex || !entry.quiz) return entry;
                                                return {
                                                  ...entry,
                                                  quiz: {
                                                    ...entry.quiz,
                                                    questions: (entry.quiz.questions || []).map((entryQuestion, i) => {
                                                      if (i !== questionIndex) return entryQuestion;
                                                      const options = [...(entryQuestion.options || [])];
                                                      options[optionIndex] = event.target.value;
                                                      return { ...entryQuestion, options };
                                                    }),
                                                  },
                                                };
                                              });
                                              updateModule(moduleIndex, { ...module, lessons });
                                            }}
                                          />
                                          <button
                                            type="button"
                                            className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[10px] font-semibold text-slate-500 transition hover:bg-slate-50"
                                            onClick={() => {
                                              const lessons = module.lessons.map((entry, idx) => {
                                                if (idx !== lessonIndex || !entry.quiz) return entry;
                                                return {
                                                  ...entry,
                                                  quiz: {
                                                    ...entry.quiz,
                                                    questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                                      i === questionIndex
                                                        ? { ...entryQuestion, options: (entryQuestion.options || []).filter((_, oi) => oi !== optionIndex) }
                                                        : entryQuestion,
                                                    ),
                                                  },
                                                };
                                              });
                                              updateModule(moduleIndex, { ...module, lessons });
                                            }}
                                          >
                                            x
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50"
                                        onClick={() => {
                                          const lessons = module.lessons.map((entry, idx) => {
                                            if (idx !== lessonIndex || !entry.quiz) return entry;
                                            return {
                                              ...entry,
                                              quiz: {
                                                ...entry.quiz,
                                                questions: (entry.quiz.questions || []).map((entryQuestion, i) =>
                                                  i === questionIndex
                                                    ? { ...entryQuestion, options: [...(entryQuestion.options || []), ''] }
                                                    : entryQuestion,
                                                ),
                                              },
                                            };
                                          });
                                          updateModule(moduleIndex, { ...module, lessons });
                                        }}
                                      >
                                        + Opção
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                              <button
                                type="button"
                                className="rounded-lg border border-violet-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-violet-700 transition hover:bg-violet-50"
                                onClick={() => {
                                  const lessons = module.lessons.map((entry, idx) => {
                                    if (idx !== lessonIndex || !entry.quiz) return entry;
                                    return {
                                      ...entry,
                                      quiz: {
                                        ...entry.quiz,
                                        questions: [...(entry.quiz.questions || []), emptyQuestion()],
                                      },
                                    };
                                  });
                                  updateModule(moduleIndex, { ...module, lessons });
                                }}
                              >
                                + Pergunta da avaliação
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                        </div>
                      ) : null}
                    </div>
                  ))}
                  <button type="button" className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100" onClick={() => {
                    const nextLessonIndex = module.lessons.length;
                    updateModule(moduleIndex, { ...module, lessons: [...module.lessons, emptyLesson()] });
                    setExpandedCreatorLessons((current) => ({ ...current, [`${moduleIndex}:${nextLessonIndex}`]: true }));
                  }}><FaPlus /> Adicionar aula</button>
                    </div>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                className="mt-1 inline-flex min-h-10 items-center gap-2 rounded-xl bg-[#0b3474] px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-[#0a2c62]"
                onClick={() => {
                  const nextIndex = form.modules.length;
                  setForm((prev) => ({ ...prev, modules: [...prev.modules, emptyModule()] }));
                  setExpandedCreatorModules((current) => ({ ...current, [nextIndex]: true }));
                }}
              >
                <FaPlus /> Adicionar módulo
              </button>
                </>
              ) : null}
            </div>

            <aside className="rounded-2xl border border-blue-100 bg-blue-50/40 p-4 xl:sticky xl:top-24 xl:max-h-[75vh] xl:overflow-y-auto">
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-slate-800">Meus cursos</h3>
                <p className="mt-0.5 text-xs text-slate-500">Selecione um curso para editar ou use “Novo curso” para começar outro.</p>
              </div>
              <div className="space-y-2">
                {creatorCourses.length === 0 ? <p className="text-xs text-slate-500">Nenhum curso para editar.</p> : null}
                {creatorCourses.map((course) => (
                  <button
                    key={course.id}
                    type="button"
                    onClick={() => startEditCourse(course)}
                    className={`w-full rounded-xl border p-3 text-left text-xs transition ${editingCourseId === course.id ? 'border-blue-400 bg-blue-50 shadow-sm ring-2 ring-blue-50' : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/40'}`}
                  >
                    <p className="font-semibold text-slate-700">{course.title}</p>
                    <p className="text-slate-500">{formatCourseStatus(course.status)} • {course.modules?.length || 0} módulos</p>
                  </button>
                ))}
              </div>
            </aside>
          </div>
          </div>
        </section>
      ) : null}

      {activeTab === TABS.COORDINATOR && canCoordinate ? (
        <section id={`course-panel-${TABS.COORDINATOR}`} role="tabpanel" aria-labelledby={`course-tab-${TABS.COORDINATOR}`} className="rounded-3xl border border-blue-100 bg-slate-50/50 p-3 shadow-sm sm:p-5">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                <FaUsersCog className="text-blue-600" /> Turmas e acessos
              </h2>
              <p className="mt-1 text-xs text-slate-500">Crie turmas, vincule alunos e cursos e controle liberações individuais em uma única tela.</p>
            </div>
            <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 shadow-sm">{classrooms.length} turma(s)</span>
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.15fr_.85fr]">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">{editingClassroomId ? 'Editar turma' : 'Nova turma'}</h3>
                  <p className="mt-0.5 text-xs text-slate-500">Defina os dados da turma e quem terá acesso aos cursos selecionados.</p>
                </div>
                {editingClassroomId ? <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">Editando</span> : null}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 sm:col-span-2">
                  Nome da turma
                  <input className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" placeholder="Ex.: Turma Administrativo 2026" value={classroomForm.name} onChange={(event) => setClassroomForm((prev) => ({ ...prev, name: event.target.value }))} />
                </label>
                <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 sm:col-span-2">
                  Descrição
                  <textarea className="min-h-[80px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" placeholder="Objetivo ou observações da turma" value={classroomForm.description} onChange={(event) => setClassroomForm((prev) => ({ ...prev, description: event.target.value }))} />
                </label>
                <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                  Coordenador
                  <select className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" value={classroomForm.coordinatorUserId || ''} onChange={(event) => setClassroomForm((prev) => ({ ...prev, coordinatorUserId: Number(event.target.value) || null }))}>
                    <option value="">Sem coordenador definido</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </label>
                <div className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                  Status
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['ACTIVE', 'Ativa'],
                      ['INACTIVE', 'Inativa'],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setClassroomForm((prev) => ({ ...prev, status: value }))}
                        className={`rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${classroomForm.status === value ? 'border-blue-300 bg-blue-50 text-blue-700 ring-2 ring-blue-50' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-700">Alunos da turma</p>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-blue-600">{classroomForm.studentIds.length} selecionado(s)</span>
                  </div>
                  <input
                    className="mb-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                    placeholder="Buscar aluno por nome ou e-mail"
                    value={studentSearchTerm}
                    onChange={(event) => setStudentSearchTerm(event.target.value)}
                  />
                  <div className="max-h-44 space-y-1 overflow-auto rounded-xl border border-slate-200 bg-white p-2 text-xs">
                    {filteredStudentUsers.map((u) => (
                      <label key={`student-${u.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-slate-700 hover:bg-blue-50">
                        <input type="checkbox" checked={classroomForm.studentIds.includes(u.id)} onChange={() => setClassroomForm((prev) => ({ ...prev, studentIds: toggleMultiSelect(prev.studentIds, u.id) }))} className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                        <span className="truncate">{u.name}</span>
                      </label>
                    ))}
                    {filteredStudentUsers.length === 0 ? <p className="px-2 py-3 text-center text-slate-400">Nenhum aluno encontrado.</p> : null}
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-700">Cursos da turma</p>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-blue-600">{classroomForm.courseIds.length} selecionado(s)</span>
                  </div>
                  <div className="max-h-[212px] space-y-1 overflow-auto rounded-xl border border-slate-200 bg-white p-2 text-xs">
                    {courses.map((course) => (
                      <label key={`course-access-${course.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-slate-700 hover:bg-blue-50">
                        <input type="checkbox" checked={classroomForm.courseIds.includes(course.id)} onChange={() => setClassroomForm((prev) => ({ ...prev, courseIds: toggleMultiSelect(prev.courseIds, course.id) }))} className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                        <span className="truncate">{course.title}</span>
                      </label>
                    ))}
                    {!courses.length ? <p className="px-2 py-3 text-center text-slate-400">Nenhum curso cadastrado.</p> : null}
                  </div>
                </section>
              </div>

              <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
                {editingClassroomId ? (
                  <button type="button" className="min-h-10 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50" onClick={cancelEditClassroom}>
                    Cancelar
                  </button>
                ) : null}
                <button type="button" disabled={!String(classroomForm.name || '').trim()} className="min-h-10 rounded-xl bg-[#0b3474] px-5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#0a2c62] disabled:cursor-not-allowed disabled:opacity-40" onClick={createClassroom}>
                  {editingClassroomId ? 'Salvar alterações' : 'Criar turma'}
                </button>
              </div>
            </div>

            <div className="space-y-5">
              <section className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4 shadow-sm sm:p-5">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-slate-800">Acesso individual</h3>
                  <p className="mt-0.5 text-xs text-slate-500">Libere um curso diretamente para um aluno, sem criar turma.</p>
                </div>
                <div className="space-y-3">
                  <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                    Curso
                    <select className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100" value={grantForm.courseId || ''} onChange={(event) => setGrantForm((prev) => ({ ...prev, courseId: Number(event.target.value) || null }))}>
                      <option value="">Selecione o curso</option>
                      {courses.map((course) => <option key={`grant-course-${course.id}`} value={course.id}>{course.title}</option>)}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                    Aluno
                    <select className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100" value={grantForm.userId || ''} onChange={(event) => setGrantForm((prev) => ({ ...prev, userId: Number(event.target.value) || null }))}>
                      <option value="">Selecione o aluno</option>
                      {filteredStudentUsers.map((u) => <option key={`grant-user-${u.id}`} value={u.id}>{u.name}</option>)}
                    </select>
                  </label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                      Início do acesso
                      <input className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100" type="datetime-local" value={grantForm.accessStartAt} onChange={(event) => setGrantForm((prev) => ({ ...prev, accessStartAt: event.target.value }))} />
                    </label>
                    <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
                      Fim do acesso
                      <input className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-normal text-slate-800 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100" type="datetime-local" value={grantForm.accessEndAt} onChange={(event) => setGrantForm((prev) => ({ ...prev, accessEndAt: event.target.value }))} />
                    </label>
                  </div>
                  <button type="button" disabled={!grantForm.courseId || !grantForm.userId} className="min-h-10 w-full rounded-xl bg-[#0b3474] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#0a2c62] disabled:cursor-not-allowed disabled:opacity-40" onClick={grantDirectAccess}>Conceder acesso</button>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">Turmas cadastradas</h3>
                  <span className="text-xs text-slate-400">{classrooms.length}</span>
                </div>
                <div className="max-h-[430px] space-y-2 overflow-auto pr-1">
                  {classrooms.map((classroom) => (
                    <article key={classroom.id} className="rounded-xl border border-slate-200 bg-white p-3 text-xs transition hover:border-blue-200 hover:shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-slate-800">{classroom.name}</p>
                          <p className="mt-1 line-clamp-2 text-slate-500">{classroom.description || 'Sem descrição'}</p>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${classroom.status === 'INACTIVE' ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'}`}>
                          {classroom.status === 'INACTIVE' ? 'Inativa' : 'Ativa'}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                        <span>{classroom.studentIds.length} aluno(s)</span>
                        <span>•</span>
                        <span>{classroom.courseIds.length} curso(s)</span>
                      </div>
                      <div className="mt-3 flex justify-end gap-2">
                        <button type="button" className="rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-[11px] font-semibold text-blue-700" onClick={() => startEditClassroom(classroom)}>Editar</button>
                        <button type="button" className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] font-semibold text-rose-700" onClick={() => deleteClassroom(classroom)}>Excluir</button>
                      </div>
                    </article>
                  ))}
                  {!classrooms.length ? <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-8 text-center text-xs text-slate-400">Nenhuma turma cadastrada.</div> : null}
                </div>
              </section>
            </div>
          </div>
        </section>
      ) : null}

      {materialUploadStatus.visible ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <p className="text-sm font-semibold text-slate-800">Enviando material da aula...</p>
            <p className="mt-1 truncate text-xs text-slate-500">{materialUploadStatus.fileName}</p>
            <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-[#0b3474] transition-all duration-200"
                style={{ width: `${materialUploadStatus.progress}%` }}
              />
            </div>
            <p className="mt-2 text-right text-xs font-semibold text-slate-700">
              {materialUploadStatus.progress}%
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
